/**
 * BIES Cookie Consent Manager
 * v2.0 - GDPR/RGPD Compliant + Event Buffer + Enhanced Ecommerce
 *
 * Features:
 * - Pre-consent event buffering (captures events before user decides)
 * - Enhanced Ecommerce tracking (view_item, add_to_cart, etc.)
 * - Identity resolution with persistent consent_id
 * - Full re-fire of all buffered events on consent
 */
(function() {
  'use strict';

  // FUENTE ÚNICA del content_id de Meta (gemelo de
  // bies-cdp/backend/functions/utils/meta_content_id.py). Canónico = variant_id (2026-09-02).
  // TODOS los emisores Meta del theme lo usan: ViewContent y AddToCart
  // (fireBrowserPixelForPage/fireAddToCartPixel + su sendEventToCollect al
  // server), el transformer genérico (path A fbq directo, path B payload→CAPI),
  // y en el server el CAPI. Deben producir el MISMO valor para el mismo
  // producto — es lo que hace funcionar el dedup browser↔server y el match con
  // el catálogo. La equivalencia con el server se garantiza porque SKU está
  // poblado (129/129 variants); los fallbacks (item_id/variant_id/product_id)
  // son por-contexto (el navegador nombra la variante item_id, el webhook la
  // nombra variant_id) y sólo actuarían si faltara SKU. Guard CI:
  // scripts/check_meta_content_id_guard.sh. Antes cada path elegía distinto
  // (A: sku||item_id, B: item_id||..., VC/ATC: item_id) — esa divergencia era
  // la causa raíz de los da_checks FAILED.
  function metaContentId(item) {
    // variant_id-first (revertido 2026-09-02): el catálogo Meta de BIES indexa
    // por variant_id (feed nativo Shopify), verificado en vivo en Commerce Manager
    // (retailer_id = 49746062967131 = ProductVariant) + Shopify API. El content_id
    // DEBE igualar el retailer_id del catálogo para que el match/DPA funcione, así
    // que emitimos variant_id (item_id en el navegador = variant.id) antes que SKU.
    // SKU queda como fallback sólo si faltara el variant_id.
    return String((item && (item.item_id || item.variant_id || item.sku || item.product_id)) || '').trim();
  }

  const STORAGE_KEY = 'bies_cookies_accepted';
  const STORAGE_PREFERENCE = 'bies_cookies_preference'; // 'all', 'essential', 'none'
  const STORAGE_CONSENT_ID = 'bies_consent_id'; // Unique ID for identity resolution
  const STORAGE_EVENT_BUFFER = 'bies_event_buffer'; // Pre-consent event buffer
  const COOKIE_DURATION_DAYS = 365; // Duración del consentimiento
  const EVENT_BUFFER_MAX_SIZE = 50; // Max events to buffer
  const EVENT_BUFFER_MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes max age

  const els = {
    overlay: document.getElementById('bies-cookies-overlay'),
    banner: document.getElementById('bies-cookies-banner'),
    acceptBtn: document.getElementById('bies-cookies-accept'),
    rejectBtn: document.getElementById('bies-cookies-reject'),
    settingsBtn: document.getElementById('bies-cookies-settings'),
    settingsPanel: document.getElementById('bies-cookies-settings-panel'),
    mainActions: document.getElementById('bies-cookies-main-actions'),
    saveBtn: document.getElementById('bies-cookies-save'),
    rejectAllBtn: document.getElementById('bies-cookies-reject-all'),
    analyticsToggle: document.getElementById('bies-cookie-analytics'),
    marketingToggle: document.getElementById('bies-cookie-marketing')
  };

  // ============================================
  // UUID GENERATOR (for identity resolution)
  // ============================================
  function generateUUID() {
    // RFC4122 version 4 compliant UUID
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      var r = Math.random() * 16 | 0;
      var v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  // ============================================
  // GET OR CREATE CONSENT ID
  // This ID persists forever and links all sessions
  // GDPR COMPLIANT: Only generated AFTER user consent
  // ============================================
  function getConsentId() {
    var consentId = localStorage.getItem(STORAGE_CONSENT_ID);
    if (!consentId) {
      consentId = generateUUID();
      localStorage.setItem(STORAGE_CONSENT_ID, consentId);

      // Also store in cookie for server-side access
      var expires = new Date();
      expires.setTime(expires.getTime() + (365 * 10 * 24 * 60 * 60 * 1000)); // 10 years
      document.cookie = STORAGE_CONSENT_ID + '=' + consentId + '; expires=' + expires.toUTCString() + '; path=/; SameSite=Lax';

      console.log('[BIES GDPR] New consent_id generated POST-consent:', consentId);
    }
    return consentId;
  }

  // ============================================
  // GET PRODUCT DATA FROM PAGE
  // Centraliza la extracción de datos del producto
  // Prioridad: 1) Liquid-injected, 2) ShopifyAnalytics, 3) JSON-LD, 4) DOM
  // ============================================
  function getProductDataFromPage() {
    var data = {
      item_id: '',
      item_name: '',
      item_category: '',
      item_brand: 'BIES',
      item_variant: '',
      price: 0,
      currency: 'EUR',
      sku: '',
      image_url: '',
      quantity: 1
    };

    // Method 1: Liquid-injected data (MOST RELIABLE)
    if (window.__biesProductData) {
      var p = window.__biesProductData;
      data.item_id = String(p.item_id || '');
      data.item_name = p.item_name || '';
      data.item_category = p.item_category || '';
      data.item_brand = p.item_brand || 'BIES';
      data.item_variant = p.item_variant || '';
      data.price = p.price || 0;
      data.currency = p.currency || 'EUR';
      data.sku = p.sku || '';
      data.image_url = p.image_url || '';
      console.log('[BIES Tracking] Product data from Liquid:', data.item_name);
      return data;
    }

    // Method 2: ShopifyAnalytics (fallback)
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
      var p = window.ShopifyAnalytics.meta.product;
      data.item_id = String(p.id || '');
      data.item_name = p.title || '';
      data.item_category = p.type || '';
      data.item_brand = p.vendor || 'BIES';
      data.item_variant = p.variant || '';
      data.price = p.price ? (p.price / 100) : 0;
      data.currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ? window.Shopify.currency.active : 'EUR';
      console.log('[BIES Tracking] Product data from ShopifyAnalytics:', data.item_name);
      return data;
    }

    // Method 3: JSON-LD structured data (fallback)
    var ldJson = document.querySelector('script[type="application/ld+json"]');
    if (ldJson) {
      try {
        var ld = JSON.parse(ldJson.textContent);
        if (ld['@type'] === 'Product') {
          data.item_id = ld.sku || ld.productID || '';
          data.item_name = ld.name || '';
          data.item_brand = (ld.brand && ld.brand.name) ? ld.brand.name : 'BIES';
          data.item_category = ld.category || '';
          data.price = (ld.offers && ld.offers.price) ? parseFloat(ld.offers.price) : 0;
          data.currency = (ld.offers && ld.offers.priceCurrency) ? ld.offers.priceCurrency : 'EUR';
          data.image_url = ld.image || '';
          console.log('[BIES Tracking] Product data from JSON-LD:', data.item_name);
          return data;
        }
      } catch(e) {
        console.warn('[BIES Tracking] Error parsing JSON-LD:', e);
      }
    }

    // Method 4: DOM extraction (last resort)
    var form = document.querySelector('form[action*="/cart/add"]');
    if (form) {
      var variantInput = form.querySelector('[name="id"]');
      var priceEl = document.querySelector('[data-product-price], .product__price .price-item, .price .money');

      data.item_id = variantInput ? variantInput.value : '';
      data.item_name = document.title.split(' – ')[0].split(' | ')[0].trim();
      data.price = priceEl ? parseFloat(priceEl.textContent.replace(/[^0-9.,]/g, '').replace(',', '.')) : 0;
      console.log('[BIES Tracking] Product data from DOM (fallback):', data.item_name);
    }

    // Currency from Shopify global
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      data.currency = window.Shopify.currency.active;
    }

    return data;
  }

  // ============================================
  // CONSENT_ID: Only set AFTER user accepts
  // Pre-consent: null (GDPR compliant)
  // Post-consent: UUID (persistent tracking allowed)
  // ============================================
  var CONSENT_ID = null;

  // Check if user has already consented (returning visitor)
  var hasStoredConsent = false;
  try {
    hasStoredConsent = localStorage.getItem(STORAGE_KEY) === 'true';
  } catch(e) {}

  if (hasStoredConsent) {
    // Returning visitor with consent - restore consent_id
    CONSENT_ID = getConsentId();
    window.BIES_CONSENT_ID = CONSENT_ID;
    console.log('[BIES GDPR] Returning visitor - consent_id restored:', CONSENT_ID);
  } else {
    // New visitor - no consent_id yet (GDPR compliant)
    window.BIES_CONSENT_ID = null;
    console.log('[BIES GDPR] New visitor - consent_id will be generated on consent');
  }

  // ============================================
  // spec bies-fix-external-id-visitor-identity-capi T02: experiment fields for /collect.
  // consent_id travels as its own top-level field (never in user_data.external_id, which
  // would short-circuit the backend identity-graph recovery). Degrades to today's payload
  // semantics when theme.liquid predates biesIdpArm (typeof guards).
  // ============================================
  function biesIdpCollectFields() {
    var arm = null;
    var pixelExternalId = null;
    try {
      var idp = window.BIES_IDP;
      if (idp && idp.consentId && idp.consentId === CONSENT_ID) {
        arm = idp.arm;
      } else if (typeof window.biesIdpArm === 'function') {
        arm = window.biesIdpArm(CONSENT_ID);
      }
      pixelExternalId = (idp && idp.pixelExternalId) || null;
    } catch (e) { arm = null; pixelExternalId = null; }
    return {
      consent_id: CONSENT_ID || null,
      idp_arm: arm || null,
      pixel_external_id: pixelExternalId
    };
  }

  // ============================================
  // WEB PIXELS BRIDGE
  // Reads data from Custom Web Pixel (sandbox) via cookies
  // Enriches events with customer email, checkout token, etc.
  // ============================================
  var WebPixelsBridge = {
    // Helper: read cookie and parse JSON
    getCookie: function(name) {
      var match = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
      if (!match) return null;
      try {
        return JSON.parse(decodeURIComponent(match[2]));
      } catch(e) {
        return match[2];
      }
    },

    // Get all data from Web Pixel
    getData: function() {
      return {
        email: this.getCookie('_bies_wp_email'),
        phone: this.getCookie('_bies_wp_phone'),
        checkout_token: this.getCookie('_bies_wp_checkout_token'),
        customer_id: this.getCookie('_bies_wp_customer_id'),
        is_first_order: this.getCookie('_bies_wp_is_first_order'),
        order_id: this.getCookie('_bies_wp_order_id'),
        discount_codes: this.getCookie('_bies_wp_discount_codes'),
        payment_gateway: this.getCookie('_bies_wp_payment_gateway'),
        payment_method: this.getCookie('_bies_wp_payment_method')
      };
    },

    // Enrich event with Web Pixel data
    enrichEvent: function(eventData) {
      var wpData = this.getData();

      // If customer email available (user logged in), add it
      if (wpData.email) {
        eventData.customer_email = wpData.email;
        eventData.customer_phone = wpData.phone;
        eventData.customer_id = wpData.customer_id;
        eventData.is_first_order = wpData.is_first_order;
        eventData.data_source = 'web_pixel_enriched';

        console.log('[BIES Bridge] Event enriched with customer email:', wpData.email);

        // Auto-link email to consent_id
        if (window.BiesCookies && window.BiesCookies.linkEmail) {
          window.BiesCookies.linkEmail(wpData.email);
        }
      }

      // Add discount codes
      if (wpData.discount_codes) {
        eventData.discount_codes = wpData.discount_codes;
      }

      // Add checkout token
      if (wpData.checkout_token) {
        eventData.checkout_token = wpData.checkout_token;
      }

      // Add payment info
      if (wpData.payment_gateway) {
        eventData.payment_gateway = wpData.payment_gateway;
        eventData.payment_method = wpData.payment_method;
      }

      // Add order ID
      if (wpData.order_id) {
        eventData.order_id = wpData.order_id;
      }

      return eventData;
    }
  };

  // Make global for other scripts
  window.WebPixelsBridge = WebPixelsBridge;
  console.log('[BIES Bridge] Web Pixels bridge initialized');

  // ============================================
  // CLICK IDs CAPTURE (fbclid, gclid, ttclid, msclkid) - v3.1
  // CRÍTICO para Meta attribution (High Priority desde 2024)
  // Referencia: https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc
  // Referencia URLSearchParams: https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams
  // ============================================

  /**
   * Captura Click IDs de URLs de campañas publicitarias
   *
   * Click IDs son parámetros URL que identifican clicks en anuncios:
   * - fbclid: Meta/Facebook Click ID (ej: ?fbclid=IwAR1234567890)
   * - gclid: Google Ads Click ID (ej: ?gclid=Cj0KCQ...)
   * - ttclid: TikTok Click ID (ej: ?ttclid=...)
   * - msclkid: Microsoft Ads Click ID (ej: ?msclkid=...)
   *
   * Meta cambió fbclid de "low priority" a "HIGH PRIORITY" en 2024
   * Con fbclid, Meta puede atribuir conversión con 100% certeza al anuncio
   *
   * @returns {Object} Objeto con click IDs capturados
   */
  function captureClickIDs() {
    // URLSearchParams API: https://url.spec.whatwg.org/#urlsearchparams
    // Compatibilidad: Chrome 49+, Firefox 44+, Safari 10.1+, Edge 17+
    var urlParams = new URLSearchParams(window.location.search);

    var clickIDs = {
      fbclid: urlParams.get('fbclid') || sessionStorage.getItem('_bies_fbclid') || null,
      gclid: urlParams.get('gclid') || sessionStorage.getItem('_bies_gclid') || null,
      ttclid: urlParams.get('ttclid') || sessionStorage.getItem('_bies_ttclid') || null,
      msclkid: urlParams.get('msclkid') || sessionStorage.getItem('_bies_msclkid') || null
    };

    // Persistir en sessionStorage para mantener durante toda la sesión
    // sessionStorage API: https://html.spec.whatwg.org/multipage/webstorage.html#the-sessionstorage-attribute
    // sessionStorage persiste durante navegación pero NO entre tabs
    if (clickIDs.fbclid && clickIDs.fbclid !== sessionStorage.getItem('_bies_fbclid')) {
      sessionStorage.setItem('_bies_fbclid', clickIDs.fbclid);
      console.log('[BIES v3.1] Facebook Click ID captured:', clickIDs.fbclid);
    }
    if (clickIDs.gclid && clickIDs.gclid !== sessionStorage.getItem('_bies_gclid')) {
      sessionStorage.setItem('_bies_gclid', clickIDs.gclid);
      console.log('[BIES v3.1] Google Click ID captured:', clickIDs.gclid);
    }
    if (clickIDs.ttclid && clickIDs.ttclid !== sessionStorage.getItem('_bies_ttclid')) {
      sessionStorage.setItem('_bies_ttclid', clickIDs.ttclid);
      console.log('[BIES v3.1] TikTok Click ID captured:', clickIDs.ttclid);
    }
    if (clickIDs.msclkid && clickIDs.msclkid !== sessionStorage.getItem('_bies_msclkid')) {
      sessionStorage.setItem('_bies_msclkid', clickIDs.msclkid);
      console.log('[BIES v3.1] Microsoft Click ID captured:', clickIDs.msclkid);
    }

    // Construir fbc manualmente si fbclid existe pero fbc cookie no
    // Formato fbc según Meta spec: fb.{subdomain_index}.{creation_time}.{fbclid}
    // Referencia: https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc
    if (clickIDs.fbclid) {
      clickIDs.fbc_constructed = 'fb.1.' + Date.now() + '.' + clickIDs.fbclid;
      console.log('[BIES v3.1] fbc constructed:', clickIDs.fbc_constructed);
    }

    return clickIDs;
  }

  // Ejecutar captura inmediatamente
  var CLICK_IDS = captureClickIDs();

  // Hacer global para acceso desde pollWebPixelEvents
  window.CLICK_IDS = CLICK_IDS;

  console.log('[BIES v3.1] Click IDs module initialized');

  // ============================================
  // SESSION TRACKING - v3.1
  // Track user session para behavioral context y deduplicación
  // Referencia Meta: https://www.tomaque.com/the-ultimate-guide-to-event-match-quality-facebook-pixel-meta-capi-and-conversion-api-in-2025/
  // Referencia sessionStorage: https://html.spec.whatwg.org/multipage/webstorage.html
  // ============================================

  var SessionTracker = {
    sessionId: null,
    startTime: null,
    pageCount: 0,

    /**
     * Inicializa o recupera sesión actual
     * sessionStorage persiste durante navegación pero se limpia al cerrar tab
     */
    init: function() {
      var sessionId = sessionStorage.getItem('_bies_session_id');
      var startTime = sessionStorage.getItem('_bies_session_start');

      if (!sessionId || !startTime) {
        // Nueva sesión
        sessionId = this.generateSessionId();
        startTime = Date.now().toString();

        sessionStorage.setItem('_bies_session_id', sessionId);
        sessionStorage.setItem('_bies_session_start', startTime);
        sessionStorage.setItem('_bies_session_page_count', '0');

        console.log('[BIES v3.1] New session started:', sessionId);
      } else {
        console.log('[BIES v3.1] Existing session restored:', sessionId);
      }

      this.sessionId = sessionId;
      this.startTime = parseInt(startTime);

      // Incrementar contador de páginas
      this.pageCount = parseInt(sessionStorage.getItem('_bies_session_page_count') || '0');
      this.pageCount++;
      sessionStorage.setItem('_bies_session_page_count', this.pageCount.toString());

      console.log('[BIES v3.1] Session page count:', this.pageCount);
    },

    /**
     * Genera ID único de sesión
     * Formato: sess_{timestamp}_{random}
     */
    generateSessionId: function() {
      // Math.random().toString(36) genera string alfanumérico
      // .substr(2, 9) toma 9 caracteres (omite "0.")
      return 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    },

    /**
     * Obtiene datos completos de la sesión
     * @returns {Object} Session data para Meta CAPI
     */
    getData: function() {
      var now = Date.now();
      var duration = now - this.startTime;

      return {
        // Session ID para deduplicación (confirmado por Meta)
        session_id: this.sessionId,

        // Duration en segundos (señal de engagement)
        session_duration_seconds: Math.floor(duration / 1000),

        // Page count (señal de engagement)
        session_page_count: this.pageCount,

        // Timestamp ISO para Meta
        session_start_time: new Date(this.startTime).toISOString()
      };
    }
  };

  // Inicializar sesión en page load
  SessionTracker.init();

  // Hacer global para acceso desde pollWebPixelEvents
  window.SessionTracker = SessionTracker;

  console.log('[BIES v3.1] Session tracking initialized');

  // ============================================
  // FACEBOOK COOKIES CAPTURE (fbp, fbc)
  // Referencia: https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc
  // ============================================

  /**
   * Captura cookies _fbp y _fbc de Facebook
   * Las pasa al Web Pixel via cookie bridge
   * fbp = Facebook Browser Pixel ID (identifica navegador único)
   * fbc = Facebook Click ID (identifica click en anuncio)
   */
  function getFacebookPixelIds() {
    var fbp = getCookie('_fbp');
    var fbc = getCookie('_fbc');

    if (fbp || fbc) {
      var fbIds = {
        fbp: fbp || null,
        fbc: fbc || null,
        timestamp: new Date().getTime()
      };

      // Pasar al Web Pixel via cookie bridge (legacy v3.x)
      document.cookie = '_bies_fb_ids=' + JSON.stringify(fbIds) +
                        '; path=/; max-age=60; SameSite=Lax';

      // Pasar al Custom Web Pixel v5.3+ via analytics.publish
      // El sandbox no puede leer _fbp via browser.cookie.get()
      // Este canal bypasea la limitacion del sandbox
      try {
        // spec bies-fix-external-id-visitor-identity-capi: the Custom Web Pixel sends
        // ~96 % of /collect events and cannot read bies_consent_id itself in every
        // browser, so the consent-bound person key rides this same bridge.
        var bridgeConsentId = null;
        try {
          if (CONSENT_ID && typeof marketingAllowed === 'function' && marketingAllowed()) {
            bridgeConsentId = CONSENT_ID;
          }
        } catch(e) { bridgeConsentId = null; }
        Shopify.analytics.publish('bies:cookie_enrichment', {
          fbp: fbp || null,
          fbc: fbc || null,
          consent_id: bridgeConsentId
        });
      } catch(e) {
        // Shopify.analytics puede no estar disponible en todas las paginas
      }

      console.log('[BIES Theme] Facebook IDs captured:', fbIds);
    }
  }

  /**
   * Helper: Lee cookie por nombre
   */
  function getCookie(name) {
    var nameEQ = name + "=";
    var ca = document.cookie.split(';');
    for(var i = 0; i < ca.length; i++) {
      var c = ca[i];
      while (c.charAt(0) == ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  // Ejecutar en page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', getFacebookPixelIds);
  } else {
    getFacebookPixelIds();
  }

  // Refresh cada 5 segundos (fbp/fbc pueden cambiar)
  // Referencia Meta: "you should regularly refresh their values"
  setInterval(getFacebookPixelIds, 5000);

  console.log('[BIES Theme] Facebook cookies capture initialized');

  // ============================================
  // CART BRIDGE: Sync fbp/fbc to cart attributes
  // So they arrive in orders/paid webhook payload
  // as note_attributes (survives checkout domain change)
  // ============================================

  var _cartBridgeLastFbp = null;
  var _cartBridgeLastFbc = null;

  function syncFacebookIdsToCart() {
    if (!hasConsent()) return;

    var fbp = getCookie('_fbp');
    var fbc = getCookie('_fbc');
    if (!fbp && !fbc) return;

    // Skip if values haven't changed since last sync
    if (fbp === _cartBridgeLastFbp && fbc === _cartBridgeLastFbc) return;

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        attributes: {
          _fbp: fbp || '',
          _fbc: fbc || ''
        }
      })
    }).then(function() {
      _cartBridgeLastFbp = fbp;
      _cartBridgeLastFbc = fbc;
      console.log('[BIES Cart Bridge] Synced fbp/fbc to cart attributes');
    }).catch(function(e) {
      console.warn('[BIES Cart Bridge] Sync failed:', e);
    });
  }

  // Sync on page load + every 30s (less aggressive than fbp refresh)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncFacebookIdsToCart);
  } else {
    syncFacebookIdsToCart();
  }
  setInterval(syncFacebookIdsToCart, 30000);

  // ============================================
  // TIMEZONE & DO NOT TRACK CAPTURE
  // NO disponibles en Web Pixel Sandbox
  // ============================================

  /**
   * Captura timezone del navegador
   * Ejemplo: "Europe/Madrid", "America/New_York"
   */
  function getTimezone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch(e) {
      console.warn('[BIES Theme] Could not get timezone:', e);
      return 'UTC';
    }
  }

  /**
   * Captura Do Not Track preference
   * "1" = DNT enabled, "0" = DNT disabled, "unspecified" = no preference
   */
  function getDoNotTrack() {
    if (navigator.doNotTrack) return navigator.doNotTrack;
    if (window.doNotTrack) return window.doNotTrack;
    if (navigator.msDoNotTrack) return navigator.msDoNotTrack;
    return '0';
  }

  /**
   * Captura datos adicionales del navegador y los pasa al Web Pixel
   */
  function captureAdditionalBrowserData() {
    var data = {
      timezone: getTimezone(),
      dnt: getDoNotTrack(),
      timestamp: new Date().getTime()
    };

    // Pasar al Web Pixel via cookie bridge
    document.cookie = '_bies_browser_data=' + JSON.stringify(data) +
                      '; path=/; max-age=60; SameSite=Lax';

    console.log('[BIES Theme] Browser data captured:', data);
  }

  // Ejecutar en page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', captureAdditionalBrowserData);
  } else {
    captureAdditionalBrowserData();
  }

  console.log('[BIES Theme] Browser data capture initialized');

  // ============================================
  // SHA-256 HASHING FOR META CAPI
  // Referencia: https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/customer-information-parameters
  // ============================================

  /**
   * Normaliza y hashea un string según requisitos Meta
   *
   * REQUISITOS META:
   * - Email: lowercase, trim
   * - Phone: solo dígitos, añadir código país
   * - Name: lowercase, trim
   * - Geo (city, state, zip, country): lowercase, trim, sin espacios
   *
   * @param {string} value - Valor a hashear
   * @param {string} type - Tipo: 'email', 'phone', 'name', 'geo'
   * @returns {Promise<string>} Hash SHA-256 en hexadecimal
   */
  async function hashForMeta(value, type) {
    if (!value) return '';

    var normalized = String(value);

    // Normalización según tipo
    switch(type) {
      case 'email':
        // Ejemplo Meta: "John_Smith@gmail.com" → "john_smith@gmail.com"
        normalized = normalized.toLowerCase().trim();
        break;

      case 'phone':
        // Ejemplo Meta: "(650)555-1212" → "16505551212" (con código país USA)
        // Para España: "+34 612 345 678" → "34612345678"
        normalized = normalized.replace(/[^0-9]/g, '');  // Solo dígitos
        if (!normalized.startsWith('34')) {  // Código país España
          normalized = '34' + normalized;
        }
        break;

      case 'name':
        // Ejemplo Meta: "Mary" → "mary"
        // Ejemplo UTF-8: "Valéry" → "valéry"
        normalized = normalized.toLowerCase().trim();
        break;

      case 'geo':
        // Ejemplo Meta: "Menlo Park" → "menlopark"
        // Ejemplo Meta: "New York" → "newyork"
        normalized = normalized.toLowerCase().trim().replace(/\s+/g, '');
        break;
    }

    // SHA-256 hashing
    try {
      var encoder = new TextEncoder();
      var data = encoder.encode(normalized);
      var hashBuffer = await crypto.subtle.digest('SHA-256', data);
      var hashArray = Array.from(new Uint8Array(hashBuffer));
      var hashHex = hashArray.map(function(b) {
        return b.toString(16).padStart(2, '0');
      }).join('');

      return hashHex;
    } catch(e) {
      console.error('[BIES Theme] Hashing error:', e);
      return '';
    }
  }

  /**
   * Hashea todos los user data parameters según requisitos Meta
   *
   * IMPORTANTE Meta Docs:
   * - HASH REQUIRED: em, ph, fn, ln, ge, db, ct, st, zp, country
   * - DO NOT HASH: fbp, fbc, external_id, client_user_agent, client_ip_address
   *
   * @param {Object} userData - Objeto con datos del usuario (sin hashear)
   * @returns {Promise<Object>} Objeto con datos hasheados
   */
  async function hashUserData(userData) {
    if (!userData) return {};

    var hashed = {};

    // Email (REQUIRED hashing)
    if (userData.em) {
      hashed.em = await hashForMeta(userData.em, 'email');
      console.log('[BIES Hash] Email hashed');
    }

    // Phone (REQUIRED hashing)
    if (userData.ph) {
      hashed.ph = await hashForMeta(userData.ph, 'phone');
      console.log('[BIES Hash] Phone hashed');
    }

    // First Name (REQUIRED hashing)
    if (userData.fn) {
      hashed.fn = await hashForMeta(userData.fn, 'name');
      console.log('[BIES Hash] First name hashed');
    }

    // Last Name (REQUIRED hashing)
    if (userData.ln) {
      hashed.ln = await hashForMeta(userData.ln, 'name');
      console.log('[BIES Hash] Last name hashed');
    }

    // City (REQUIRED hashing)
    if (userData.ct) {
      hashed.ct = await hashForMeta(userData.ct, 'geo');
      console.log('[BIES Hash] City hashed');
    }

    // State/Province (REQUIRED hashing)
    if (userData.st) {
      hashed.st = await hashForMeta(userData.st, 'geo');
      console.log('[BIES Hash] State hashed');
    }

    // Zip (REQUIRED hashing)
    if (userData.zp) {
      hashed.zp = await hashForMeta(userData.zp, 'geo');
      console.log('[BIES Hash] Zip hashed');
    }

    // Country (REQUIRED hashing)
    if (userData.country) {
      hashed.country = await hashForMeta(userData.country, 'geo');
      console.log('[BIES Hash] Country hashed');
    }

    // DO NOT HASH (según Meta docs)
    hashed.fbp = userData.fbp || null;
    hashed.fbc = userData.fbc || null;
    hashed.external_id = userData.external_id || null;
    hashed.client_user_agent = userData.client_user_agent || null;

    // Opcional: Marketing consent (NO se hashea)
    if (typeof userData.email_marketing_consent !== 'undefined') {
      hashed.email_marketing_consent = userData.email_marketing_consent;
    }
    if (typeof userData.sms_marketing_consent !== 'undefined') {
      hashed.sms_marketing_consent = userData.sms_marketing_consent;
    }

    console.log('[BIES Hash] User data hashing complete');
    return hashed;
  }

  console.log('[BIES Theme] SHA-256 hashing functions initialized');

  // ============================================
  // WEB PIXELS EVENT POLLING
  // Escucha eventos del Custom Web Pixel via cookie bridge
  // ============================================
  var lastEventTimestamp = 0;

  async function pollWebPixelEvents() {
    var eventCookie = WebPixelsBridge.getCookie('_bies_wp_last_event');

    if (!eventCookie || eventCookie.timestamp <= lastEventTimestamp) {
      return;
    }

    lastEventTimestamp = eventCookie.timestamp;

    var eventName = eventCookie.name;
    var rawData = eventCookie.data;

    console.log('[BIES Web Pixels Polling] Received event from pixel:', eventName);

    // Validar que tenemos datos válidos
    if (!rawData || !eventName) {
      console.warn('[BIES Polling] Invalid event data - skipping');
      return;
    }

    // ============================================
    // ENRIQUECER DATOS CON FBP/FBC
    // ============================================
    var fbIdsCookie = getCookie('_bies_fb_ids');
    if (fbIdsCookie) {
      try {
        var fbIds = JSON.parse(fbIdsCookie);
        rawData.user_data = rawData.user_data || {};
        rawData.user_data.fbp = fbIds.fbp;
        rawData.user_data.fbc = fbIds.fbc;
        console.log('[BIES Polling] Facebook IDs injected:', fbIds);
      } catch(e) {
        console.error('[BIES Polling] Error parsing fbIds:', e);
      }
    }

    // ============================================
    // ENRIQUECER DATOS CON TIMEZONE/DNT
    // ============================================
    var browserDataCookie = getCookie('_bies_browser_data');
    if (browserDataCookie) {
      try {
        var browserData = JSON.parse(browserDataCookie);
        rawData.device_fingerprint = rawData.device_fingerprint || {};
        rawData.device_fingerprint.timezone = browserData.timezone;
        rawData.device_fingerprint.dnt = browserData.dnt;
        console.log('[BIES Polling] Browser data injected:', browserData);
      } catch(e) {
        console.error('[BIES Polling] Error parsing browser data:', e);
      }
    }

    // ============================================
    // ENRIQUECER CON CLICK IDs - v3.1 (CRÍTICO)
    // fbclid = High Priority para Meta EMQ desde 2024
    // ============================================
    if (window.CLICK_IDS) {
      rawData.user_data = rawData.user_data || {};

      // Si tenemos fbclid pero no fbc, usar fbc construido
      if (window.CLICK_IDS.fbclid && !rawData.user_data.fbc) {
        rawData.user_data.fbc = window.CLICK_IDS.fbc_constructed;
        console.log('[BIES v3.1] fbc injected from fbclid:', rawData.user_data.fbc);
      }

      // Guardar click IDs en attribution data para análisis multi-plataforma
      rawData.attribution_data = rawData.attribution_data || {};
      rawData.attribution_data.fbclid = window.CLICK_IDS.fbclid;
      rawData.attribution_data.gclid = window.CLICK_IDS.gclid;
      rawData.attribution_data.ttclid = window.CLICK_IDS.ttclid;
      rawData.attribution_data.msclkid = window.CLICK_IDS.msclkid;

      if (window.CLICK_IDS.fbclid) {
        console.log('[BIES v3.1] Meta Click ID injected - High Priority parameter');
      }
    }

    // ============================================
    // ENRIQUECER CON SESSION DATA - v3.1
    // session_id = confirmado para deduplicación por Meta
    // ============================================
    if (window.SessionTracker) {
      rawData.session_data = SessionTracker.getData();
      console.log('[BIES v3.1] Session data injected:', rawData.session_data);
    }

    // ============================================
    // HASHEAR USER_DATA (Meta CAPI Requirements)
    // ============================================
    if (rawData.user_data &&
        (rawData.user_data.em || rawData.user_data.fn)) {

      console.log('[BIES Polling] Hashing user data...');

      try {
        rawData.user_data = await hashUserData(rawData.user_data);
        console.log('[BIES Polling] User data hashed successfully');
      } catch(e) {
        console.error('[BIES Polling] Error hashing user data:', e);
      }
    }

    // ============================================
    // GENERAR EVENT_ID (Deduplicación Pixel vs CAPI)
    // Referencia: https://www.facebook.com/business/help/823677331451951
    // ============================================
    var eventId = eventName + '_' + eventCookie.timestamp;
    if (rawData.user_data && rawData.user_data.external_id) {
      eventId += '_' + rawData.user_data.external_id;
    } else {
      eventId += '_no_id';
    }
    rawData.event_id = eventId;

    console.log('[BIES Polling] Event ID generated:', eventId);

    // ============================================
    // VERIFICAR CONSENTIMIENTO Y ENVIAR
    // ============================================
    if (!hasConsent()) {
      // NO consent: bufferear evento
      EventBuffer.addEvent(eventName, rawData);
      console.log('[BIES Web Pixels Polling] Event buffered (no consent yet)');
    } else {
      // CON consent: enviar directamente a plataformas
      EventBuffer.sendToAllPlatforms({
        name: eventName,
        data: rawData,
        timestamp: eventCookie.timestamp
      });
      console.log('[BIES Web Pixels Polling] Event sent to platforms (consent granted)');
    }
  }

  // Iniciar polling cada 100ms
  setInterval(pollWebPixelEvents, 100);
  console.log('[BIES Web Pixels Polling] Started polling for Web Pixel events (v3.1)');

  // ============================================
  // EVENT BUFFER SYSTEM
  // Captures ecommerce events BEFORE consent is given
  // Flushes to all platforms when consent granted
  // ============================================
  var EventBuffer = {
    // Supported event types for ecommerce (GA4 Enhanced Ecommerce + Custom)
    // https://developers.google.com/analytics/devguides/collection/ga4/reference/events
    // https://developers.facebook.com/docs/meta-pixel/reference
    SUPPORTED_EVENTS: [
      // Ecommerce Events
      'view_item',           // ✅ Meta
      'view_item_list',      // ✅ Meta (custom)
      'select_item',         // ✅ Meta (custom)
      'add_to_cart',         // ✅ Meta
      'remove_from_cart',    // ✅ Meta (custom)
      'view_cart',           // ✅ Meta (custom)
      'begin_checkout',      // ✅ Meta
      'add_shipping_info',   // ✅ Meta (skip)
      'add_payment_info',    // ✅ Meta
      'purchase',            // ✅ Meta
      'refund',              // ✅ Meta (custom)
      'search',              // ✅ Meta
      'add_to_wishlist',     // ✅ Meta

      // Marketing Events (NEW v2.7)
      'generate_lead',       // ✅ Meta Lead
      'sign_up',             // ✅ Meta CompleteRegistration
      'subscribe',           // ✅ Meta Subscribe
      'contact',             // ✅ Meta Contact

      // Engagement Events
      'customize_product',   // ✅ Meta CustomizeProduct
      'view_promotion',      // ✅ Meta (skip)
      'select_promotion',    // ✅ Meta (skip)
      'share'                // ✅ Meta (custom)
    ],

    // Get buffer from sessionStorage
    getBuffer: function() {
      try {
        var buffer = sessionStorage.getItem(STORAGE_EVENT_BUFFER);
        return buffer ? JSON.parse(buffer) : [];
      } catch(e) {
        return [];
      }
    },

    // Save buffer to sessionStorage
    saveBuffer: function(buffer) {
      try {
        sessionStorage.setItem(STORAGE_EVENT_BUFFER, JSON.stringify(buffer));
      } catch(e) {
        console.warn('[BIES EventBuffer] Failed to save buffer:', e);
      }
    },

    // Add event to buffer
    addEvent: function(eventName, eventData) {
      // Don't buffer if consent already given
      if (hasConsent()) return false;

      var buffer = this.getBuffer();

      // Check max size
      if (buffer.length >= EVENT_BUFFER_MAX_SIZE) {
        buffer.shift(); // Remove oldest event
      }

      // 🔥 ENRICH with Web Pixels data (customer email, checkout token, etc.)
      eventData = eventData || {};
      if (window.WebPixelsBridge) {
        eventData = WebPixelsBridge.enrichEvent(eventData);
      }

      var event = {
        id: generateUUID(),
        name: eventName,
        data: eventData,
        timestamp: Date.now(),
        page: {
          url: window.location.href,
          path: window.location.pathname,
          title: document.title,
          referrer: document.referrer
        }
      };

      buffer.push(event);
      this.saveBuffer(buffer);

      console.log('[BIES EventBuffer] Buffered event:', eventName, event);
      return true;
    },

    // Flush buffer - send all events to tracking platforms
    flushBuffer: function() {
      var buffer = this.getBuffer();
      if (buffer.length === 0) {
        console.log('[BIES EventBuffer] No buffered events to flush');
        return;
      }

      var now = Date.now();
      var validEvents = buffer.filter(function(event) {
        return (now - event.timestamp) < EVENT_BUFFER_MAX_AGE_MS;
      });

      console.log('[BIES EventBuffer] Flushing', validEvents.length, 'events');

      validEvents.forEach(function(event) {
        EventBuffer.sendToAllPlatforms(event);
      });

      // Clear buffer after flush
      this.clearBuffer();

      // Dispatch custom event for external listeners
      if (typeof window.CustomEvent === 'function') {
        window.dispatchEvent(new CustomEvent('bies:buffer-flushed', {
          detail: { eventsCount: validEvents.length, events: validEvents }
        }));
      }
    },

    // Send single event to all tracking platforms
    // ============================================
    // BIES v3.2 - NEUTRAL DATA TRANSFORMERS
    // ============================================
    // ARCHITECTURE: Web Pixel sends NEUTRAL data
    // Theme transforms for each specific pixel
    // GOAL: Maximize Meta EMQ + Full compatibility
    // ============================================

    /**
     * 📊 GA4 TRANSFORMER (DISABLED - Only Meta Pixel active)
     * DISABLED 2025-11-27: Only Meta Pixel active for now
     * GA4 uses neutral format almost unchanged
     */
    // sendToGA4: function(eventName, eventData) {
    //   if (!window.gtag) return;

    //   var ga4Data = {
    //     currency: eventData.currency,
    //     value: eventData.value
    //   };

    //   // Items array (neutral format compatible with GA4)
    //   if (eventData.items && eventData.items.length > 0) {
    //     ga4Data.items = eventData.items;
    //   }

    //   // Transaction-specific fields
    //   if (eventName === 'purchase') {
    //     if (eventData.transaction_id) ga4Data.transaction_id = eventData.transaction_id;
    //     if (eventData.tax) ga4Data.tax = eventData.tax;
    //     if (eventData.shipping) ga4Data.shipping = eventData.shipping;
    //     if (eventData.coupon) ga4Data.coupon = eventData.coupon;

    //     // ===== CUSTOMER BEHAVIORAL DIMENSIONS v3.2.2 =====
    //     if (typeof eventData.new_customer === 'boolean') {
    //       ga4Data.new_customer = eventData.new_customer;
    //     }

    //     if (typeof eventData.customer_order_count === 'number') {
    //       ga4Data.customer_lifetime_orders = eventData.customer_order_count;
    //     }

    //     if (typeof eventData.is_subscribed === 'boolean') {
    //       ga4Data.customer_subscription_status = eventData.is_subscribed ? 'subscribed' : 'not_subscribed';
    //     }

    //     if (typeof eventData.accepts_marketing === 'boolean') {
    //       ga4Data.customer_marketing_consent = eventData.accepts_marketing ? 'yes' : 'no';
    //     }

    //     if (typeof eventData.customer_account_age_days === 'number') {
    //       ga4Data.customer_account_age_days = eventData.customer_account_age_days;
    //     }

    //     if (typeof eventData.customer_total_spent === 'number') {
    //       ga4Data.customer_lifetime_value = eventData.customer_total_spent;
    //     }

    //     if (typeof eventData.avg_order_value === 'number') {
    //       ga4Data.customer_avg_order_value = eventData.avg_order_value;
    //     }
    //   }

    //   // List fields for view_item_list
    //   if (eventName === 'view_item_list') {
    //     if (eventData.item_list_id) ga4Data.item_list_id = eventData.item_list_id;
    //     if (eventData.item_list_name) ga4Data.item_list_name = eventData.item_list_name;
    //   }

    //   gtag('event', eventName, ga4Data);
    //   console.log('[BIES v3.2.2] GA4 sent:', eventName, ga4Data);
    // },

    /**
     * 🔵 META TRANSFORMER (CRITICAL FOR EMQ)
     * Transforms neutral data to Meta CAPI format
     * Builds content_ids, contents[], num_items, etc.
     */
    sendToMeta: function(eventName, eventData) {
      if (!window.fbq) return;
      if (!marketingAllowed()) { console.log('[BIES GDPR] Meta send blocked (task 03):', eventName); return; }

      // Event name mapping: neutral → Meta
      var fbEventMap = {
        'view_item': 'ViewContent',
        'view_item_list': 'ViewContent',
        'add_to_cart': 'AddToCart',
        'remove_from_cart': 'RemoveFromCart',
        'view_cart': 'ViewCart',
        'begin_checkout': 'InitiateCheckout',
        'add_payment_info': 'AddPaymentInfo',
        'add_shipping_info': 'AddShippingInfo',
        'purchase': 'Purchase',
        'search': 'Search',

        // Marketing events (custom)
        'generate_lead': 'Lead',
        'sign_up': 'CompleteRegistration',
        'subscribe': 'Subscribe',
        'contact': 'Contact',
        'customize_product': 'CustomizeProduct'
      };

      var fbEvent = fbEventMap[eventName];
      if (!fbEvent) {
        // Non-standard event → use trackCustom
        window.biesFbqTrack('trackCustom', eventName, {
          value: eventData.value || 0,
          currency: eventData.currency || 'EUR'
        });
        console.log('[BIES v3.2] Meta custom event:', eventName);
        return;
      }

      // ===== BUILD META DATA FROM NEUTRAL SCHEMA =====
      var metaData = {};

      // Marketing/Engagement events (different structure)
      if (fbEvent === 'Lead' || fbEvent === 'CompleteRegistration' ||
          fbEvent === 'Subscribe' || fbEvent === 'Contact' ||
          fbEvent === 'CustomizeProduct') {

        metaData.currency = eventData.currency || 'EUR';
        metaData.value = eventData.value || 0;

        if (fbEvent === 'Lead') {
          metaData.content_name = eventData.form_name || eventData.content_name || '';
          metaData.content_category = eventData.form_type || 'lead';
        }

        if (fbEvent === 'CompleteRegistration') {
          metaData.status = eventData.status !== false;
          metaData.content_name = eventData.registration_method || 'email';
        }

        if (fbEvent === 'Subscribe') {
          if (eventData.predicted_ltv) metaData.predicted_ltv = eventData.predicted_ltv;
          metaData.content_name = eventData.subscription_type || 'subscription';
        }

        if (fbEvent === 'Contact') {
          metaData.content_name = eventData.method || 'contact';
        }

        if (fbEvent === 'CustomizeProduct') {
          metaData.content_name = eventData.variant_name || eventData.option_name || '';
          metaData.content_category = eventData.product_name || '';
        }

        // Clean empty fields
        Object.keys(metaData).forEach(function(key) {
          if (metaData[key] === '' || metaData[key] === 0) {
            delete metaData[key];
          }
        });

      } else {
        // ===== ECOMMERCE EVENTS (MAIN TRANSFORMATION) =====

        // 1. CONTENT_TYPE (always 'product')
        metaData.content_type = 'product';

        // 2. CONTENT_IDS - Extract SKUs from items array
        if (eventData.items && eventData.items.length > 0) {
          metaData.content_ids = eventData.items.map(metaContentId);
        }

        // 3. CONTENTS - Build Meta contents array
        // Format: [{id, quantity, item_price, delivery_category}]
        if (eventData.items && eventData.items.length > 0) {
          metaData.contents = eventData.items.map(function(item) {
            var content = {
              id: metaContentId(item),
              quantity: item.quantity || 1
            };

            // Add item_price (HIGHLY RECOMMENDED)
            if (typeof item.price === 'number') {
              content.item_price = item.price;
            }

            // Add delivery_category for purchase events
            if (eventData.delivery_category) {
              content.delivery_category = eventData.delivery_category;
            }

            return content;
          });
        }

        // 4. VALUE & CURRENCY
        if (typeof eventData.value === 'number') {
          metaData.value = eventData.value;
        }
        if (eventData.currency) {
          metaData.currency = eventData.currency;
        }

        // 5. NUM_ITEMS (REQUIRED for InitiateCheckout, recommended for Purchase)
        if (fbEvent === 'InitiateCheckout' || fbEvent === 'Purchase' || fbEvent === 'AddPaymentInfo') {
          var totalItems = 0;
          if (eventData.items && eventData.items.length > 0) {
            totalItems = eventData.items.reduce(function(sum, item) {
              return sum + (item.quantity || 1);
            }, 0);
          }
          metaData.num_items = totalItems;
        }

        // 6. CONTENT_NAME & CONTENT_CATEGORY (for single product events)
        if (eventData.content_name) {
          metaData.content_name = eventData.content_name;
        }
        if (eventData.content_category) {
          metaData.content_category = eventData.content_category;
        }

        // 7. SEARCH_STRING (for Search events)
        if (fbEvent === 'Search' && eventData.search_string) {
          metaData.search_string = eventData.search_string;
        }

        // 8. DELIVERY_CATEGORY
        if (eventData.delivery_category) {
          metaData.delivery_category = eventData.delivery_category;
        }

        // ===== 9. CUSTOMER BEHAVIORAL DATA v3.2.2 =====
        if (fbEvent === 'Purchase') {
          // Core behavioral fields (always sent)
          if (typeof eventData.new_customer === 'boolean') {
            metaData.new_customer = eventData.new_customer;
          }

          if (typeof eventData.customer_order_count === 'number') {
            metaData.customer_order_count = eventData.customer_order_count;
          }

          if (typeof eventData.is_subscribed === 'boolean') {
            metaData.is_subscribed = eventData.is_subscribed;
          }

          if (typeof eventData.accepts_marketing === 'boolean') {
            metaData.accepts_marketing = eventData.accepts_marketing;
          }

          if (typeof eventData.customer_account_age_days === 'number') {
            metaData.customer_account_age_days = eventData.customer_account_age_days;
          }

          // Conditional fields (progressive enhancement)
          if (typeof eventData.customer_total_spent === 'number') {
            metaData.customer_total_spent = eventData.customer_total_spent;
          }

          if (typeof eventData.avg_order_value === 'number') {
            metaData.avg_order_value = eventData.avg_order_value;
          }
        }

        // Remove undefined/empty fields
        Object.keys(metaData).forEach(function(key) {
          if (metaData[key] === '' || metaData[key] === undefined) {
            delete metaData[key];
          }
        });
      }

      // Pass event_id for dedup with CAPI (Meta deduplicates browser+server by event_id)
      var fbqOptions = {};
      if (eventData.event_id) {
        fbqOptions.eventID = eventData.event_id;
      }
      window.biesFbqTrack('track', fbEvent, metaData, fbqOptions);
      console.log('[BIES v3.2.2] Meta sent:', fbEvent, metaData);
    },

    /**
     * 📡 BIES COLLECT ENDPOINT
     * Sends event to /collect backend for CAPI enrichment + profile storage.
     * Uses navigator.sendBeacon for fire-and-forget (survives page unload).
     * Backend saves profile (fbp, fbc, session) for webhook Purchase enrichment.
     */
    sendToCollect: function(eventName, eventData) {
      if (!marketingAllowed()) { console.log('[BIES GDPR] Collect send blocked (task 03):', eventName); return; }
      // Event name mapping: neutral → Meta (same as sendToMeta)
      var collectEventMap = {
        'page_view': 'PageView',
        'view_item': 'ViewContent',
        'view_item_list': 'ViewContent',
        'add_to_cart': 'AddToCart',
        'remove_from_cart': 'RemoveFromCart',
        'view_cart': 'ViewCart',
        'begin_checkout': 'InitiateCheckout',
        'add_payment_info': 'AddPaymentInfo',
        'add_shipping_info': 'AddShippingInfo',
        'purchase': 'Purchase',
        'search': 'Search'
      };

      var metaEventName = collectEventMap[eventName];
      if (!metaEventName) return; // Skip unmapped events

      // Don't send Purchase via /collect (webhook handles it as source of truth)
      if (metaEventName === 'Purchase') return;

      // Build payload matching /collect expected format
      var idpFields = biesIdpCollectFields();
      var payload = {
        event_name: metaEventName,
        event_id: eventData.event_id || (eventName + '_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6)),
        event_time: Math.floor(Date.now() / 1000),
        event_source_url: window.location.href,
        session_id: (window.SessionTracker && window.SessionTracker.sessionId) || null,
        consent_granted: hasConsent(),
        consent_id: idpFields.consent_id,
        idp_arm: idpFields.idp_arm,
        pixel_external_id: idpFields.pixel_external_id,
        user_data: {
          fbp: (eventData.user_data && eventData.user_data.fbp) || null,
          fbc: (eventData.user_data && eventData.user_data.fbc) || null,
          em: (eventData.user_data && eventData.user_data.em) || null,
          ph: (eventData.user_data && eventData.user_data.ph) || null,
          fn: (eventData.user_data && eventData.user_data.fn) || null,
          ln: (eventData.user_data && eventData.user_data.ln) || null,
          external_id: (eventData.user_data && eventData.user_data.external_id) || null,
          client_user_agent: navigator.userAgent
        },
        custom_data: {},
        context: {
          checkout_token: (eventData.checkout_token) || null,
          utm_source: (window.CLICK_IDS && window.CLICK_IDS.utm_source) || null,
          utm_medium: (window.CLICK_IDS && window.CLICK_IDS.utm_medium) || null,
          utm_campaign: (window.CLICK_IDS && window.CLICK_IDS.utm_campaign) || null,
          referrer_url: document.referrer || null
        },
        customer: {}
      };

      // Build custom_data based on event type
      if (eventData.value) payload.custom_data.value = parseFloat(eventData.value);
      if (eventData.currency) payload.custom_data.currency = eventData.currency;
      if (eventData.items && eventData.items.length > 0) {
        payload.custom_data.content_ids = eventData.items.map(metaContentId).filter(Boolean);
        payload.custom_data.content_type = 'product';
        payload.custom_data.contents = eventData.items.map(function(item) {
          return {
            id: metaContentId(item),
            quantity: item.quantity || 1,
            item_price: parseFloat(item.price) || 0
          };
        }).filter(function(c) { return c.id; });
        payload.custom_data.num_items = eventData.items.reduce(function(sum, item) {
          return sum + (item.quantity || 1);
        }, 0);
      }

      // Build customer data
      if (eventData.customer_id) payload.customer.id = String(eventData.customer_id);

      // Remove null/undefined values from user_data
      Object.keys(payload.user_data).forEach(function(key) {
        if (payload.user_data[key] === null || payload.user_data[key] === undefined) {
          delete payload.user_data[key];
        }
      });

      // Send via navigator.sendBeacon (fire-and-forget, survives page unload)
      var COLLECT_URL = 'https://collect-2dwmk4g42a-ew.a.run.app/collect';
      var jsonPayload = JSON.stringify(payload);

      try {
        if (navigator.sendBeacon) {
          var blob = new Blob([jsonPayload], { type: 'application/json' });
          var sent = navigator.sendBeacon(COLLECT_URL, blob);
          if (!sent) {
            // Fallback to fetch if sendBeacon queue is full
            fetch(COLLECT_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: jsonPayload,
              keepalive: true
            }).catch(function() {});
          }
        } else {
          // Fallback for older browsers
          fetch(COLLECT_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: jsonPayload,
            keepalive: true
          }).catch(function() {});
        }
        console.log('[BIES v3.2] Collect sent:', metaEventName, payload.event_id);
      } catch(e) {
        // Silent fail - never block user experience for tracking
        console.error('[BIES Collect] Error:', e.message);
      }
    },

    /**
     * 🎵 TIKTOK TRANSFORMER
     * Transforms neutral data to TikTok format
     */
    sendToTikTok: function(eventName, eventData) {
      // TODO: Uncomment when TikTok Pixel is installed
      /*
      if (!window.ttq) return;

      var ttEventMap = {
        'view_item': 'ViewContent',
        'add_to_cart': 'AddToCart',
        'begin_checkout': 'InitiateCheckout',
        'add_payment_info': 'AddPaymentInfo',
        'purchase': 'CompletePayment'
      };

      var ttEvent = ttEventMap[eventName];
      if (!ttEvent) return;

      // TikTok uses first item for single values
      var firstItem = eventData.items && eventData.items[0];

      var ttData = {
        content_type: 'product',
        content_id: firstItem ? String(firstItem.sku || firstItem.item_id) : '',
        content_name: firstItem ? firstItem.item_name : '',
        content_category: firstItem ? firstItem.item_category : '',
        quantity: firstItem ? (firstItem.quantity || 1) : 1,
        price: firstItem ? (firstItem.price || 0) : 0,
        value: eventData.value || 0,
        currency: eventData.currency || 'EUR'
      };

      // Build contents array (similar to Meta)
      if (eventData.items && eventData.items.length > 0) {
        ttData.contents = eventData.items.map(function(item) {
          return {
            content_id: String(item.sku || item.item_id),
            content_name: item.item_name || '',
            content_category: item.item_category || '',
            quantity: item.quantity || 1,
            price: item.price || 0
          };
        });
      }

      ttq.track(ttEvent, ttData);
      console.log('[BIES v3.2] TikTok sent:', ttEvent, ttData);
      */
    },

    /**
     * 📊 SHOPIFY ANALYTICS TRANSFORMER (DISABLED)
     * DISABLED 2025-11-27: Not called anymore (see line 1600)
     * Reason: ShopifyAnalytics.lib.track() is undocumented API
     * Shopify Admin Analytics already has all data from native sources
     * Send to Shopify native analytics
     */
    sendToShopifyAnalytics: function(eventName, eventData) {
      if (!window.ShopifyAnalytics || !window.ShopifyAnalytics.lib) return;

      try {
        var shopifyEventMap = {
          'view_item': 'Viewed Product',
          'add_to_cart': 'Added Product',
          'remove_from_cart': 'Removed Product',
          'view_cart': 'Viewed Cart',
          'begin_checkout': 'Started Checkout',
          'purchase': 'Completed Purchase',
          'search': 'Searched',
          'view_item_list': 'Viewed Collection'
        };

        var shopifyEvent = shopifyEventMap[eventName];
        if (!shopifyEvent) return;

        var shopifyData = {};

        if (eventData.items && eventData.items[0]) {
          var firstItem = eventData.items[0];
          shopifyData.productId = firstItem.item_id;
          shopifyData.productName = firstItem.item_name;
          shopifyData.productCategory = firstItem.item_category;
          shopifyData.productBrand = firstItem.item_brand;
          shopifyData.productVariant = firstItem.item_variant;
          shopifyData.sku = firstItem.sku;
          shopifyData.price = firstItem.price;
          shopifyData.quantity = firstItem.quantity;
        }

        if (eventData.value) shopifyData.value = eventData.value;
        if (eventData.currency) shopifyData.currency = eventData.currency;
        if (eventData.transaction_id) shopifyData.transactionId = eventData.transaction_id;

        ShopifyAnalytics.lib.track(shopifyEvent, shopifyData);
        console.log('[BIES v3.2] Shopify Analytics sent:', shopifyEvent);
      } catch(e) {
        console.warn('[BIES v3.2] Shopify Analytics error:', e);
      }
    },

    /**
     * 🏷️ DATALAYER TRANSFORMER
     * Send to DataLayer for GTM
     */
    sendToDataLayer: function(eventName, eventData) {
      if (!window.dataLayer) return;

      window.dataLayer.push({
        'event': eventName,
        'ecommerce': eventData
      });

      console.log('[BIES v3.2] DataLayer sent:', eventName);
    },

    /**
     * 🎯 MAIN FUNCTION: sendToAllPlatforms
     * Reads NEUTRAL data and transforms for each pixel
     */
    sendToAllPlatforms: function(event) {
      var eventName = event.name;        // Neutral name: 'view_item'
      var eventData = event.data;        // Neutral data (complete schema)

      // Add enrichment metadata (consent, buffering info)
      var enrichedData = Object.assign({}, eventData, {
        consent_id: CONSENT_ID,
        buffered: event.buffered || false,
        original_timestamp: event.timestamp ? new Date(event.timestamp).toISOString() : new Date().toISOString(),
        sent_timestamp: new Date().toISOString()
      });

      if (event.timestamp) {
        enrichedData.delay_seconds = Math.round((Date.now() - event.timestamp) / 1000);
      }

      // ===== SEND TO ALL PLATFORMS WITH TRANSFORMATIONS =====

      // 1. Google Analytics 4 (DISABLED - Only Meta active)
      // DISABLED 2025-11-27: Only Meta Pixel active for now
      // this.sendToGA4(eventName, enrichedData);

      // 2. Meta Pixel (CRITICAL transformation for EMQ)
      this.sendToMeta(eventName, enrichedData);

      // 3. BIES Collect Endpoint (CAPI enrichment + profile storage)
      this.sendToCollect(eventName, enrichedData);

      // 4. TikTok Pixel (when installed)
      this.sendToTikTok(eventName, enrichedData);

      // 4. Shopify Analytics (DISABLED - Redundant with native tracking)
      // DISABLED 2025-11-27: ShopifyAnalytics.lib.track() is undocumented API
      // Shopify Admin Analytics feeds from:
      //   - Orders (backend database) - already tracked
      //   - Pageviews (native Trekkie) - already tracked
      // Custom events via ShopifyAnalytics.lib.track():
      //   - Don't appear in Admin Analytics reports as individual events
      //   - Only show as aggregated metrics (which come from backend anyway)
      //   - Custom data (predicted_ltv, delivery_category) not used in dashboard
      // Conclusion: Redundant call, Shopify already has all the data it needs
      // this.sendToShopifyAnalytics(eventName, enrichedData);

      // 6. DataLayer for GTM (DISABLED - GTM not installed)
      // DISABLED 2025-11-27: GTM not currently installed in the store
      // DataLayer prepared for future use when/if GTM is installed for testing/experimentation
      // To enable: Uncomment line below after installing GTM container
      // Current architecture uses direct pixel calls (sendToGA4, sendToMeta) for maximum EMQ
      // this.sendToDataLayer(eventName, enrichedData);

      console.log('[BIES v3.2] Event distributed to all platforms:', eventName);
    },

    // Clear buffer
    clearBuffer: function() {
      try {
        sessionStorage.removeItem(STORAGE_EVENT_BUFFER);
        console.log('[BIES EventBuffer] Buffer cleared');
      } catch(e) {}
    },

    // Get buffer stats
    getStats: function() {
      var buffer = this.getBuffer();
      return {
        count: buffer.length,
        events: buffer.map(function(e) { return e.name; }),
        oldestAge: buffer.length > 0 ? Math.round((Date.now() - buffer[0].timestamp) / 1000) + 's' : null
      };
    }
  };

  // Expose EventBuffer globally
  window.BiesEventBuffer = EventBuffer;

  // ============================================
  // CUSTOM EVENTS LISTENERS
  // Solo eventos que NO cubre el Web Pixel
  // (Web Pixel cubre los 10 core events automáticamente)
  // ============================================
  function setupCustomEventListeners() {
    // Skip if already consented
    if (hasConsent()) {
      console.log('[BIES Custom Events] Consent already given, skipping buffer listeners');
      return;
    }

    console.log('[BIES Custom Events] Setting up custom event listeners');

    // ---- INTERCEPT DATALAYER PUSHES ----
    // Solo para eventos custom que NO están en los 10 core
    if (window.dataLayer && typeof window.dataLayer.push === 'function') {
      var originalPush = window.dataLayer.push;
      window.dataLayer.push = function() {
        try {
          var args = Array.prototype.slice.call(arguments);

          args.forEach(function(arg) {
            if (arg && arg.event && EventBuffer.SUPPORTED_EVENTS.indexOf(arg.event) !== -1) {
              EventBuffer.addEvent(arg.event, arg.ecommerce || arg);
            }
          });
        } catch(e) {
          console.error('[BIES EventBuffer] dataLayer interceptor error:', e);
        }

        return originalPush.apply(window.dataLayer, arguments);
      };
    }

    // NOTA: Los siguientes eventos YA los maneja el Web Pixel automáticamente:
    // - view_item (product_viewed)
    // - add_to_cart (product_added_to_cart)
    // - remove_from_cart (product_removed_from_cart)
    // - view_cart (cart_viewed)
    // - begin_checkout (checkout_started)
    // - add_payment_info (payment_info_submitted)
    // - purchase (checkout_completed)
    // - search (search_submitted)
    // - view_item_list (collection_viewed)
    // - page_view (page_viewed)

    // Solo mantenemos tracking de eventos CUSTOM que Web Pixel NO cubre:
    // - select_item (click en producto en colección)
    // - customize_product (cambio de variante)
    // - generate_lead (newsletter signup)
    // - sign_up (account creation)
    // - subscribe (Club BIES)
    // - contact (contact forms)

    // ---- SELECT ITEM TRACKING (custom) ----
    if (window.location.pathname.includes('/collections/')) {
      setupSelectItemTracking();
    }
  }

  // NOTE: Las siguientes funciones fueron ELIMINADAS porque ahora las maneja el Web Pixel:
  // - captureCurrentProduct() → Web Pixel product_viewed
  // - captureAddToCart() → Web Pixel product_added_to_cart
  // - interceptCartAjax() → Web Pixel product_added_to_cart
  // - captureCollectionView() → Web Pixel collection_viewed
  // - setupCartViewTracking() → Web Pixel cart_viewed
  // - setupRemoveFromCartTracking() → Web Pixel product_removed_from_cart
  // Total eliminado: ~145 líneas de código redundante

  // ============================================
  // CUSTOM EVENT TRACKING FUNCTIONS
  // Solo eventos que NO cubre el Web Pixel automáticamente
  // ============================================

  // Track select_item (product clicks in collections)
  function setupSelectItemTracking() {
    setTimeout(function() {
      var productCards = document.querySelectorAll('[data-product-id], .product-card, .grid__item .card, .product-item');

      productCards.forEach(function(card, index) {
        card.addEventListener('click', function(e) {
          // Avoid tracking if clicking on "Add to cart" button
          if (e.target.closest('.btn, button, [type="submit"]')) return;

          var productId = card.getAttribute('data-product-id') || '';
          var titleEl = card.querySelector('.card__heading, .product-card__title, h3 a, .product-title');
          var priceEl = card.querySelector('.price .money, .product-card__price, .product-price');

          // Get collection name from page
          var collectionName = '';
          if (window.__biesCollectionData && window.__biesCollectionData.collection_name) {
            collectionName = window.__biesCollectionData.collection_name;
          } else {
            collectionName = document.title.split(' – ')[0].split(' | ')[0].trim();
          }

          EventBuffer.addEvent('select_item', {
            item_id: String(productId),
            item_name: titleEl ? titleEl.textContent.trim() : '',
            item_list_name: collectionName,
            item_list_id: window.location.pathname,
            index: index,
            price: priceEl ? parseFloat(priceEl.textContent.replace(/[^0-9.,]/g, '').replace(',', '.')) : 0,
            currency: 'EUR'
          });

          console.log('[BIES Tracking] Product selected:', productId);
        });
      });

      console.log('[BIES Tracking] Select item tracking enabled on', productCards.length, 'products');
    }, 1000);
  }

  // ============================================
  // META PIXEL CRITICAL EVENTS (NEW v2.7)
  // Auto-tracking for high-value marketing events
  // ============================================

  // ---- NEWSLETTER SIGNUP (Lead event) ----
  function setupNewsletterTracking() {
    // Track all newsletter form submissions
    document.addEventListener('submit', function(e) {
      var form = e.target;

      // Common newsletter form patterns
      if (form.action && (
          form.action.includes('/contact') ||
          form.action.includes('/newsletter') ||
          form.classList.contains('newsletter-form') ||
          form.id === 'newsletter-form' ||
          form.querySelector('input[name*="newsletter"]') ||
          form.querySelector('input[name="contact[email]"]')
        )) {

        var emailInput = form.querySelector('input[type="email"]');
        var email = emailInput ? emailInput.value : '';

        EventBuffer.addEvent('generate_lead', {
          form_name: 'newsletter',
          form_type: 'subscription',
          content_name: 'Newsletter Signup',
          value: 0.50,  // Estimated value of a newsletter subscriber
          currency: 'EUR'
        });

        console.log('[BIES Tracking] Newsletter signup detected');

        // If consent already given, link email for better matching
        if (email && CONSENT_ID) {
          BiesCookies.linkEmail(email);
        }
      }
    });

    console.log('[BIES Tracking] Newsletter signup tracking enabled');
  }

  // ---- ACCOUNT CREATION (CompleteRegistration event) ----
  function setupAccountCreationTracking() {
    // Detect successful account creation
    // Shopify redirects from /account/register to /account on success
    if (window.location.pathname === '/account' ||
        window.location.pathname.includes('/account')) {

      // Check if coming from registration
      var referrer = document.referrer || '';
      var fromRegister = referrer.includes('/register') ||
                        referrer.includes('/account/register') ||
                        sessionStorage.getItem('bies_just_registered') === 'true';

      if (fromRegister) {
        EventBuffer.addEvent('sign_up', {
          registration_method: 'email',
          status: true,
          value: 2.00,  // Registered users are more valuable
          currency: 'EUR'
        });

        console.log('[BIES Tracking] Account creation detected');

        // Clear flag
        sessionStorage.removeItem('bies_just_registered');
      }
    }

    // Set flag when visiting register page
    if (window.location.pathname.includes('/register')) {
      sessionStorage.setItem('bies_just_registered', 'true');
    }

    console.log('[BIES Tracking] Account creation tracking enabled');
  }

  // ---- CONTACT EVENTS (Contact event) ----
  function setupContactTracking() {
    // Track contact form submissions
    document.addEventListener('submit', function(e) {
      var form = e.target;

      if (form.action && (
          form.action.includes('/contact') &&
          !form.action.includes('/newsletter')  // Avoid double-tracking newsletter
        )) {

        EventBuffer.addEvent('contact', {
          method: 'contact_form',
          content_name: 'Contact Form',
          value: 1.00,  // Contact intent is valuable
          currency: 'EUR'
        });

        console.log('[BIES Tracking] Contact form submission detected');
      }
    });

    // Track chat widget opens (if you have chat)
    // Tidio, Intercom, Zendesk, etc.
    if (window.tidioChatApi) {
      window.tidioChatApi.on('open', function() {
        EventBuffer.addEvent('contact', {
          method: 'chat',
          content_name: 'Chat Opened',
          value: 0.75,
          currency: 'EUR'
        });
        console.log('[BIES Tracking] Chat opened');
      });
    }

    // Intercom
    if (window.Intercom) {
      window.Intercom('onShow', function() {
        EventBuffer.addEvent('contact', {
          method: 'chat',
          content_name: 'Intercom Chat Opened',
          value: 0.75,
          currency: 'EUR'
        });
        console.log('[BIES Tracking] Intercom chat opened');
      });
    }

    console.log('[BIES Tracking] Contact tracking enabled');
  }

  // ---- CLUB BIES SIGNUP (Subscribe event) ----
  function setupClubBiesTracking() {
    // Detect Club BIES page and signup
    if (window.location.pathname.includes('/club-bies') ||
        window.location.pathname.includes('/pages/club')) {

      // Track form submissions on this page
      document.addEventListener('submit', function(e) {
        var form = e.target;

        // Any form on club page is likely signup
        EventBuffer.addEvent('subscribe', {
          subscription_type: 'club_bies',
          content_name: 'Club BIES Signup',
          value: 10.00,  // Club members are very valuable
          predicted_ltv: 100.00,  // Estimated lifetime value
          currency: 'EUR'
        });

        console.log('[BIES Tracking] Club BIES signup detected');
      });

      console.log('[BIES Tracking] Club BIES signup tracking enabled');
    }
  }

  // ---- PRODUCT CUSTOMIZATION (CustomizeProduct event) ----
  function setupCustomizeProductTracking() {
    // Only on product pages
    if (window.location.pathname.includes('/products/') &&
        window.__biesProductData) {

      // Track variant selector changes
      setTimeout(function() {
        var variantSelectors = document.querySelectorAll(
          'input[name="id"],' +           // Radio buttons
          'select[name="id"],' +          // Dropdown
          '.product-form__option input,' + // Swatch inputs
          '[name^="option"]'              // Any option inputs
        );

        variantSelectors.forEach(function(selector) {
          selector.addEventListener('change', function() {
            var selectedOption = this.value;
            var optionName = this.name || 'variant';
            var variantText = this.getAttribute('data-variant-title') ||
                            this.options ? this.options[this.selectedIndex].text : selectedOption;

            EventBuffer.addEvent('customize_product', {
              product_id: window.__biesProductData.item_id,
              product_name: window.__biesProductData.item_name,
              option_name: optionName,
              variant_name: variantText,
              variant_id: selectedOption,
              value: 0.25,  // Small value for engagement
              currency: 'EUR'
            });

            console.log('[BIES Tracking] Product customized:', variantText);
          });
        });

        console.log('[BIES Tracking] Product customization tracking enabled on', variantSelectors.length, 'selectors');
      }, 500);
    }
  }

  // Initialize all new trackers
  try {
    setupNewsletterTracking();
    setupAccountCreationTracking();
    setupContactTracking();
    setupClubBiesTracking();
    setupCustomizeProductTracking();
    setupAddToCartBrowserPixel();
  } catch(e) {
    console.warn('[BIES Tracking] Error initializing meta events:', e);
  }

  // ============================================
  // COLLECT USER SIGNALS (for identity matching)
  // ============================================
  function collectUserSignals() {
    return {
      consent_id: CONSENT_ID,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screen_resolution: screen.width + 'x' + screen.height,
      viewport: window.innerWidth + 'x' + window.innerHeight,
      platform: navigator.platform,
      referrer: document.referrer || 'direct',
      landing_page: window.location.pathname,
      utm_source: new URLSearchParams(window.location.search).get('utm_source') || null,
      utm_medium: new URLSearchParams(window.location.search).get('utm_medium') || null,
      utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || null
    };
  }

  // ============================================
  // FUNCIONES DE ALMACENAMIENTO
  // ============================================

  function hasConsent() {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  }

  // spec bies-theme-rendimiento-cookies-y-senal-v1 task 03: marketing-purpose gate.
  // hasConsent() only says the banner was answered. A visitor who accepted analytics
  // only (bies_cookies_marketing === 'false') must never reach Meta (fbq) or /collect.
  // Non-EU auto-grant and legacy non-granular accepts have no stored 'false' → allowed.
  function marketingAllowed() {
    try {
      // The stored answer is written by setConsent() before trackConsentEvent() re-fires
      // PageView, so it is the reliable source at that instant; window._biesConsentState
      // is only updated at the end of trackConsentEvent(). 'revoked' still wins.
      if (window._biesConsentState === 'revoked') return false;
      if (!hasConsent()) return false;
      if (localStorage.getItem('bies_cookies_marketing') === 'false') return false;
      return true;
    } catch (e) { return false; }
  }
  window.biesMarketingAllowed = marketingAllowed;

  function setConsent(accepted, preference = 'all', granular = null) {
    try {
      localStorage.setItem(STORAGE_KEY, accepted ? 'true' : 'false');
      localStorage.setItem(STORAGE_PREFERENCE, preference);

      // Store granular preferences if provided
      if (granular) {
        localStorage.setItem('bies_cookies_analytics', granular.analytics ? 'true' : 'false');
        localStorage.setItem('bies_cookies_marketing', granular.marketing ? 'true' : 'false');
      }

      // También setear cookie para backend (si se necesita)
      const expires = new Date();
      expires.setTime(expires.getTime() + (COOKIE_DURATION_DAYS * 24 * 60 * 60 * 1000));
      document.cookie = `${STORAGE_KEY}=${accepted}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

      // Determine actual consent per category
      var analyticsConsent = granular ? granular.analytics : accepted;
      var marketingConsent = granular ? granular.marketing : accepted;

      // Event tracking + Shopify consent (handled inside trackConsentEvent)
      trackConsentEvent(preference, granular);

      // ============================================
      // CONSENT ANALYTICS (Firebase endpoint)
      // Fire-and-forget - NEVER blocks consent flow
      // ============================================
      try {
        var _consentAnalytics = granular ? granular.analytics : accepted;
        var _consentMarketing = granular ? granular.marketing : accepted;
        var _signals = collectUserSignals();
        fetch('https://consent-2dwmk4g42a-ew.a.run.app', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            consent_id: CONSENT_ID || getConsentId(),
            accepted: !!accepted,
            preference: preference || 'unknown',
            analytics: !!_consentAnalytics,
            marketing: !!_consentMarketing,
            is_eu: window._biesIsEU !== false,
            country: window._biesCountry || 'unknown',
            user_signals: {
              language: _signals.language,
              timezone: _signals.timezone,
              screen_resolution: _signals.screen_resolution,
              viewport: _signals.viewport,
              platform: _signals.platform,
              referrer: _signals.referrer,
              landing_page: window.location.pathname,
              utm_source: _signals.utm_source || '',
              utm_medium: _signals.utm_medium || '',
              utm_campaign: _signals.utm_campaign || ''
            }
          }),
          keepalive: true
        });
      } catch(_e) { /* silent - never affects consent flow */ }

    } catch (e) {
      console.error('Error saving cookie consent:', e);
    }
  }

  function trackConsentEvent(preference, granular) {
    var consentGranted = preference === 'all' || preference === 'custom';
    var analyticsGranted = granular ? granular.analytics : consentGranted;
    var marketingGranted = granular ? granular.marketing : consentGranted;

    var analyticsValue = analyticsGranted ? 'granted' : 'denied';
    var marketingValue = marketingGranted ? 'granted' : 'denied';

    // ============================================
    // GOOGLE CONSENT MODE V2 (Complete params)
    // ============================================
    if (window.gtag) {
      gtag('consent', 'update', {
        'ad_storage': marketingValue,
        'ad_user_data': marketingValue,
        'ad_personalization': marketingValue,
        'analytics_storage': analyticsValue,
        'functionality_storage': 'granted',
        'personalization_storage': analyticsValue,
        'security_storage': 'granted'  // Always granted for security
      });
      console.log('[BIES GDPR] Google consent updated - analytics:', analyticsValue, '| marketing:', marketingValue);
    }

    // ============================================
    // GOOGLE TAG MANAGER (DataLayer event)
    // DISABLED 2025-11-27: GTM not installed
    // ============================================
    // if (window.dataLayer) {
    //   var userSignals = collectUserSignals();
    //   window.dataLayer.push({
    //     'event': 'cookie_consent_update',
    //     'consent_id': CONSENT_ID,
    //     'consent_preference': preference,
    //     'consent_analytics': consentGranted,
    //     'consent_marketing': consentGranted,
    //     'consent_personalization': consentGranted,
    //     'user_signals': userSignals
    //   });
    // }

    // ============================================
    // FACEBOOK PIXEL
    // ============================================
    if (window.fbq) {
      fbq('consent', marketingGranted ? 'grant' : 'revoke');
      console.log('[BIES GDPR] Facebook Pixel consent:', marketingGranted ? 'granted' : 'revoked');
    }

    // ============================================
    // TIKTOK PIXEL (if present)
    // TODO: Descomentar cuando TikTok Pixel esté instalado
    // ============================================
    /*
    if (window.ttq) {
      if (marketingGranted) {
        ttq.enableCookie();
      } else {
        ttq.disableCookie();
      }
    }
    */

    // ============================================
    // SHOPIFY CUSTOMER PRIVACY API
    // ============================================
    if (window.Shopify && window.Shopify.customerPrivacy) {
      try {
        window.Shopify.customerPrivacy.setTrackingConsent({
          analytics: analyticsGranted,
          marketing: marketingGranted,
          preferences: true,
          sale_of_data: marketingGranted
        }, function() {
          console.log('[BIES GDPR] Shopify customerPrivacy updated');
        });
      } catch(e) {
        console.warn('[BIES GDPR] Shopify customerPrivacy error:', e);
      }
    }

    // ============================================
    // RE-FIRE PAGEVIEW EVENTS (prevent data loss)
    // When consent changes from denied to granted,
    // we need to re-send the PageView so it's properly tracked
    // ============================================
    if (analyticsGranted || marketingGranted) {
      // Small delay to ensure consent is processed first
      setTimeout(function() {
        var userSignals = collectUserSignals();

        // Google Analytics - re-fire page_view with consent_id
        // DISABLED 2025-11-27: GA4 not active
        // if (window.gtag) {
        //   gtag('event', 'page_view', {
        //     page_title: document.title,
        //     page_location: window.location.href,
        //     page_path: window.location.pathname,
        //     consent_id: CONSENT_ID,
        //     consent_given: true,
        //     consent_timestamp: new Date().toISOString()
        //   });

        //   // Set user_id for cross-session tracking
        //   gtag('set', 'user_properties', {
        //     consent_id: CONSENT_ID
        //   });

        //   console.log('[BIES GDPR] GA PageView re-fired with consent_id:', CONSENT_ID);
        // }

        // Google Analytics 4 - send config with user properties (only if ID is defined)
        // DISABLED 2025-11-27: GA4 not active
        // if (window.gtag && window.GA_MEASUREMENT_ID) {
        //   gtag('config', window.GA_MEASUREMENT_ID, {
        //     'anonymize_ip': false,
        //     'cookie_flags': 'SameSite=None;Secure',
        //     'user_id': CONSENT_ID
        //   });
        // }

        // Facebook Pixel - re-fire PageView with consent_id (only if marketing accepted)
        if (window.fbq && marketingGranted) {
          // task 05: reuse the page-level id minted in theme.liquid <head>
          var pvEventId = window.__bies_pageview_event_id || ('pv_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9));
          try {
            Shopify.analytics.publish('bies:event_id_sync', {
              event_name: 'PageView',
              event_id: pvEventId
            });
          } catch(e) { /* Web Pixel fallback */ }
          // spec T02: person-id init must precede the track (Meta keeps the first init with userData)
          var idp = null;
          try {
            if (typeof window.biesIdpPixelInit === 'function') { idp = window.biesIdpPixelInit(CONSENT_ID); }
          } catch(e) { idp = null; }
          var pvCustomData = {
            consent_id: CONSENT_ID,
            consent_given: true
          };
          if (idp && (idp.arm === 'A' || idp.arm === 'B')) { pvCustomData.idp_arm = idp.arm; }
          window.biesFbqTrack('track', 'PageView', pvCustomData, {eventID: pvEventId});
          sendEventToCollect('PageView', pvEventId, {});
          // Also set external_id for advanced matching (status quo; skipped when the
          // person-id init above already gave the pixel its userData)
          if (!(idp && idp.pixelExternalId)) {
            // Shadow datasets A/A: same init for production + B + B0 (helper in theme.liquid <head>)
            if (typeof window.biesFbqInitAll === 'function') {
              window.biesFbqInitAll(window.FB_PIXEL_ID || null, {
                external_id: CONSENT_ID
              });
            } else {
              fbq('init', window.FB_PIXEL_ID || null, {
                external_id: CONSENT_ID
              });
            }
          }
          console.log('[BIES GDPR] FB PageView re-fired with consent_id + collect sent:', CONSENT_ID);
        }

        // TikTok - re-fire page view if available
        // TODO: Descomentar cuando TikTok Pixel esté instalado
        /*
        if (window.ttq && typeof window.ttq.page === 'function') {
          ttq.page();
          console.log('[BIES GDPR] TikTok PageView re-fired after consent');
        }
        */

        // Shopify Analytics - ensure tracking is active
        // DISABLED 2025-11-27: Redundant with native Trekkie pageview tracking
        // Shopify already captures pageviews via Trekkie (automatic)
        // if (window.ShopifyAnalytics) {
        //   try {
        //     window.ShopifyAnalytics.lib.track('Viewed Page', {
        //       consent_id: CONSENT_ID,
        //       consent_given: true,
        //       url: window.location.href
        //     });
        //     console.log('[BIES GDPR] Shopify Analytics PageView tracked with consent_id');
        //   } catch(e) {
        //     console.warn('[BIES GDPR] Shopify Analytics error:', e);
        //   }
        // }

        console.log('[BIES GDPR] All PageViews re-fired with consent_id:', CONSENT_ID);
      }, 100);
    }

    // Update global consent state
    window._biesConsentState = consentGranted ? 'granted' : 'revoked';
    // Narrow consent signal for purpose-bound adapters. It carries no consent ID
    // or customer data and lets them abort/forget immediately on revocation.
    window.dispatchEvent(new CustomEvent('bies:consentchange', { detail: {
      analytics: analyticsGranted === true,
      marketing: marketingGranted === true,
      purpose: 'analytics'
    } }));

    console.log('[BIES GDPR] Consent recorded:', preference, '| Analytics:', analyticsGranted ? 'ON' : 'OFF', '| Marketing:', marketingGranted ? 'ON' : 'OFF');
  }

  // ============================================
  // UI FUNCTIONS
  // ============================================

  // spec bies-theme-rendimiento-cookies-y-senal-v1 task 02: while the sheet is
  // visible, other fixed bottom UI (sticky add-to-cart) reads
  // `html.bies-consent-open` + `--bies-consent-height` to lift itself above it.
  // The property lives on <html> because the sticky bar is not a descendant of
  // the banner wrapper. Fallback 180px = the sheet's mobile max-height.
  function syncConsentHeight() {
    var root = document.documentElement;
    var height = 180;
    if (els.banner) {
      var measured = els.banner.getBoundingClientRect().height;
      if (measured > 0) height = Math.ceil(measured);
    }
    root.style.setProperty('--bies-consent-height', height + 'px');
  }

  function showBanner() {
    if (!els.overlay) return;

    // Mostrar banner con animación
    requestAnimationFrame(() => {
      els.overlay.classList.add('show');
      document.documentElement.classList.add('bies-consent-open');
      syncConsentHeight();

      // spec bies-theme-rendimiento-cookies-y-senal-v1 task 02: the banner is a
      // non-blocking bottom sheet (role="dialog", aria-modal="false"). Move focus
      // to its first button so keyboard/screen-reader users land on it without
      // scrolling the page (preventScroll). Never throws: focus is best-effort.
      if (els.acceptBtn && typeof els.acceptBtn.focus === 'function') {
        try {
          els.acceptBtn.focus({ preventScroll: true });
        } catch (_e) {
          try { els.acceptBtn.focus(); } catch (_e2) { /* silent */ }
        }
      }
    });
  }

  function hideBanner() {
    if (!els.overlay) return;

    // Ocultar banner. Every close path (accept, reject, save, reject-all) goes
    // through here, so this is the single place that releases the sticky bar.
    els.overlay.classList.remove('show');
    document.documentElement.classList.remove('bies-consent-open');
    document.documentElement.style.removeProperty('--bies-consent-height');

    // Remover del DOM después de animación
    setTimeout(() => {
      if (els.overlay && els.overlay.parentNode) {
        els.overlay.style.display = 'none';
      }
    }, 300);
  }

  // ============================================
  // EVENT HANDLERS
  // ============================================

  function handleAccept() {
    // ============================================
    // GENERATE CONSENT_ID NOW (user has consented)
    // ============================================
    CONSENT_ID = getConsentId();
    window.BIES_CONSENT_ID = CONSENT_ID;
    console.log('[BIES Cookies] User accepted all cookies - consent_id generated:', CONSENT_ID);

    // Collect all user signals at moment of consent
    var userSignals = collectUserSignals();

    // Get buffer stats before consent
    var bufferStats = EventBuffer.getStats();
    console.log('[BIES Cookies] Event buffer stats:', bufferStats);

    // Track the accept action before setting consent
    // DISABLED 2025-11-27: GTM not installed
    // if (window.dataLayer) {
    //   window.dataLayer.push({
    //     'event': 'cookie_consent_accepted',
    //     'consent_id': CONSENT_ID,
    //     'consent_method': 'accept_all',
    //     'timestamp': new Date().toISOString(),
    //     'user_signals': userSignals,
    //     'buffered_events_count': bufferStats.count
    //   });
    // }

    setConsent(true, 'all');
    hideBanner();

    // Force reload analytics scripts if they were blocked
    // This ensures maximum data capture
    setTimeout(function() {
      // Trigger a custom event that other scripts can listen to
      if (typeof window.CustomEvent === 'function') {
        var event = new CustomEvent('bies:consent-granted', {
          detail: {
            consent_id: CONSENT_ID,
            preference: 'all',
            timestamp: Date.now(),
            user_signals: userSignals,
            buffered_events_count: bufferStats.count
          }
        });
        window.dispatchEvent(event);
      }

      // ============================================
      // FLUSH EVENT BUFFER - Send all pre-consent events
      // This is critical for capturing all user actions
      // that happened before they clicked "Accept"
      // ============================================
      setTimeout(function() {
        console.log('[BIES Cookies] Flushing event buffer after consent...');
        EventBuffer.flushBuffer();
      }, 200); // Additional delay to ensure all tracking is ready

    }, 50);

    // Re-publish _fbp after fbevents.js creates it (needs ~500ms)
    setTimeout(function() {
      if (typeof getFacebookPixelIds === 'function') {
        getFacebookPixelIds();
        console.log('[BIES Browser Pixel] Re-published _fbp after consent grant');
      }
    }, 500);

    // Fire browser pixel ViewContent for product pages
    fireBrowserPixelForPage();

    // Opcional: recargar para activar scripts que esperan consentimiento
    // window.location.reload();
  }

  // Rejections used to reach consent_events without utm or landing page, so
  // acceptance could not be split by campaign or by Instagram traffic (action
  // 2026-09-28-1709). Same shape as the accept path in setConsent(); these are
  // the visitor's own URL and device signals, no identifier and no cookie.
  function buildRejectionSignals() {
    try {
      var _signals = collectUserSignals();
      return {
        language: _signals.language,
        timezone: _signals.timezone,
        screen_resolution: _signals.screen_resolution,
        viewport: _signals.viewport,
        platform: _signals.platform,
        referrer: _signals.referrer,
        landing_page: window.location.pathname,
        utm_source: _signals.utm_source || '',
        utm_medium: _signals.utm_medium || '',
        utm_campaign: _signals.utm_campaign || ''
      };
    } catch (_e) {
      return {};
    }
  }

  function handleReject() {
    console.log('[BIES Cookies] User dismissed without accepting (session-only)');

    var bufferStats = EventBuffer.getStats();
    console.log('[BIES Cookies] Discarding', bufferStats.count, 'buffered events');

    // Session-only: banner will return on next session
    try { sessionStorage.setItem('bies_cookies_dismissed', '1'); } catch(e) {}

    // Signal denied consent to Shopify for this pageview
    if (window.Shopify && window.Shopify.customerPrivacy) {
      window.Shopify.customerPrivacy.setTrackingConsent({
        analytics: false, marketing: false,
        preferences: true, sale_of_data: false
      }, function() {
        console.log('[BIES GDPR] Session denial signaled to Shopify');
      });
    }

    // Record rejection in Firebase (fire-and-forget, never blocks UI)
    try {
      fetch('https://consent-2dwmk4g42a-ew.a.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          consent_id: CONSENT_ID || null,
          accepted: false,
          preference: 'essential',
          analytics: false,
          marketing: false,
          is_eu: window._biesIsEU !== false,
          country: window._biesCountry || 'unknown',
          user_signals: buildRejectionSignals()
        }),
        keepalive: true
      });
    } catch(_e) { /* silent - never affects consent flow */ }

    window._biesConsentState = 'revoked';
    window.dispatchEvent(new CustomEvent('bies:consentchange', { detail: { analytics: false, marketing: false, purpose: 'analytics' } }));
    hideBanner();
    EventBuffer.clearBuffer();
    clearThirdPartyCookies();
  }

  function handleSettings() {
    if (!els.settingsPanel) return;

    var isOpen = els.settingsPanel.classList.contains('show');
    if (isOpen) {
      els.settingsPanel.classList.remove('show');
      els.settingsBtn.textContent = 'Configurar';
    } else {
      els.settingsPanel.classList.add('show');
      els.settingsBtn.textContent = 'Ocultar opciones';
    }

    // spec bies-theme-rendimiento-cookies-y-senal-v1 task 02: the initial
    // bottom sheet is capped at 180px (mobile). `settings-open` on the wrapper
    // lifts that cap (CSS in bies-cookies-banner.liquid) so the granular panel
    // is fully usable; aria-expanded mirrors the panel state for AT users.
    if (els.overlay) {
      els.overlay.classList.toggle('settings-open', !isOpen);
    }
    if (els.settingsBtn) {
      els.settingsBtn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    }
    // The sheet height changes with the panel: re-measure for the sticky bar.
    requestAnimationFrame(syncConsentHeight);
    console.log('[BIES Cookies] Settings panel', isOpen ? 'closed' : 'opened');
  }

  function handleSavePreferences() {
    var analyticsAccepted = els.analyticsToggle ? els.analyticsToggle.checked : false;
    var marketingAccepted = els.marketingToggle ? els.marketingToggle.checked : false;

    var granular = {
      analytics: analyticsAccepted,
      marketing: marketingAccepted
    };

    // At least one non-essential category accepted = consent true
    var anyAccepted = analyticsAccepted || marketingAccepted;

    if (anyAccepted) {
      CONSENT_ID = getConsentId();
      window.BIES_CONSENT_ID = CONSENT_ID;
    }

    var preference = (analyticsAccepted && marketingAccepted) ? 'all' :
                     anyAccepted ? 'custom' : 'essential';

    console.log('[BIES Cookies] User saved preferences - analytics:', analyticsAccepted, '| marketing:', marketingAccepted);

    setConsent(anyAccepted, preference, granular);
    hideBanner();

    if (anyAccepted) {
      // Flush buffer for accepted categories
      setTimeout(function() {
        if (typeof window.CustomEvent === 'function') {
          window.dispatchEvent(new CustomEvent('bies:consent-granted', {
            detail: {
              consent_id: CONSENT_ID,
              preference: preference,
              analytics: analyticsAccepted,
              marketing: marketingAccepted,
              timestamp: Date.now()
            }
          }));
        }

        setTimeout(function() {
          EventBuffer.flushBuffer();
        }, 200);
      }, 50);
    } else {
      // No categories accepted = session-only dismissal
      try { sessionStorage.setItem('bies_cookies_dismissed', '1'); } catch(e) {}
      if (window.Shopify && window.Shopify.customerPrivacy) {
        window.Shopify.customerPrivacy.setTrackingConsent({
          analytics: false, marketing: false,
          preferences: true, sale_of_data: false
        }, function() {});
      }
      EventBuffer.clearBuffer();
      clearThirdPartyCookies();
    }
  }

  function handleRejectAll() {
    console.log('[BIES Cookies] User rejected all from settings (session-only)');

    var bufferStats = EventBuffer.getStats();
    console.log('[BIES Cookies] Discarding', bufferStats.count, 'buffered events');

    // Session-only: banner will return on next session
    try { sessionStorage.setItem('bies_cookies_dismissed', '1'); } catch(e) {}

    // Signal denied consent to Shopify for this pageview
    if (window.Shopify && window.Shopify.customerPrivacy) {
      window.Shopify.customerPrivacy.setTrackingConsent({
        analytics: false, marketing: false,
        preferences: true, sale_of_data: false
      }, function() {
        console.log('[BIES GDPR] Session denial signaled to Shopify');
      });
    }

    // Record rejection in Firebase (fire-and-forget, never blocks UI)
    try {
      fetch('https://consent-2dwmk4g42a-ew.a.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          consent_id: CONSENT_ID || null,
          accepted: false,
          preference: 'essential',
          analytics: false,
          marketing: false,
          is_eu: window._biesIsEU !== false,
          country: window._biesCountry || 'unknown',
          user_signals: buildRejectionSignals()
        }),
        keepalive: true
      });
    } catch(_e) { /* silent - never affects consent flow */ }

    window._biesConsentState = 'revoked';
    window.dispatchEvent(new CustomEvent('bies:consentchange', { detail: { analytics: false, marketing: false, purpose: 'analytics' } }));
    hideBanner();
    EventBuffer.clearBuffer();
    clearThirdPartyCookies();
  }

  function clearThirdPartyCookies() {
    // Limpiar cookies de analytics, ads, etc. y consent_id si existía
    const cookiesToClear = ['_ga', '_gid', '_gat', '_fbp', '_ttp', STORAGE_CONSENT_ID];

    cookiesToClear.forEach(name => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
    try { localStorage.removeItem(STORAGE_CONSENT_ID); } catch(e) {}
    CONSENT_ID = null;
  }

  // ============================================
  // CHAIN BREAK DETECTION
  // Checks if interceptor chain is working correctly
  // ============================================
  function detectChainBreak() {
    setTimeout(function() {
      try {
        var warnings = [];

        // Check fetch chain
        if (typeof window.fetch === 'function') {
          var fetchStr = window.fetch.toString();
          if (!fetchStr.includes('originalFetch') && !fetchStr.includes('native code')) {
            warnings.push('fetch interceptor chain may be broken');
          }
        }

        // Check dataLayer chain
        if (window.dataLayer && typeof window.dataLayer.push === 'function') {
          var pushStr = window.dataLayer.push.toString();
          if (!pushStr.includes('originalPush') && !pushStr.includes('native code')) {
            warnings.push('dataLayer.push interceptor chain may be broken');
          }
        }

        // Log warnings if any
        if (warnings.length > 0) {
          console.warn('[BIES EventBuffer] Interceptor chain warnings:', warnings);
          console.warn('[BIES EventBuffer] This may indicate another script is overwriting interceptors');
        } else {
          console.log('[BIES EventBuffer] Interceptor chain integrity: OK');
        }
      } catch(e) {
        // Silent fail - detection is optional
        console.log('[BIES EventBuffer] Chain detection skipped (not critical)');
      }
    }, 2000); // Run after other scripts load
  }

  // ============================================
  // BROWSER PIXEL — ViewContent & AddToCart
  // Fires fbq() directly for Meta Custom Audiences
  // CAPI alone does NOT populate Website Custom Audiences
  // ============================================

  /**
   * Send event to /collect backend for CAPI enrichment + Profile Store.
   * Called alongside fbq() from browser pixel functions.
   * Reads fbp/fbc from cookies, session from SessionTracker, customer from Liquid.
   * Uses same event_id as fbq() for Meta deduplication (browser + server = 1 event).
   */
  function sendEventToCollect(metaEventName, eventId, customData) {
    if (!marketingAllowed()) return; // task 03: marketing purpose, not just "banner answered"
    if (metaEventName === 'PageView') {
      window._biesCollectPageViewSent = true;
    }

    // Read fbp/fbc directly from browser cookies
    var fbp = getCookie('_fbp') || null;
    var fbc = getCookie('_fbc') || null;

    // Fallback: if _fbc cookie not yet created by Meta Pixel but fbclid was in URL,
    // use manually constructed fbc (same pattern as pollWebPixelEvents lines 1219-1221)
    if (!fbc && window.CLICK_IDS && window.CLICK_IDS.fbc_constructed) {
      fbc = window.CLICK_IDS.fbc_constructed;
    }

    var idpFields = biesIdpCollectFields();
    var payload = {
      event_name: metaEventName,
      event_id: eventId,
      event_time: Math.floor(Date.now() / 1000),
      event_source_url: window.location.href,
      session_id: (window.SessionTracker && window.SessionTracker.sessionId) || null,
      consent_granted: true,
      consent_id: idpFields.consent_id,
      idp_arm: idpFields.idp_arm,
      pixel_external_id: idpFields.pixel_external_id,
      user_data: {
        fbp: fbp,
        fbc: fbc,
        client_user_agent: navigator.userAgent
      },
      custom_data: customData || {},
      context: {
        utm_source: (window.CLICK_IDS && window.CLICK_IDS.utm_source) || null,
        utm_medium: (window.CLICK_IDS && window.CLICK_IDS.utm_medium) || null,
        utm_campaign: (window.CLICK_IDS && window.CLICK_IDS.utm_campaign) || null,
        referrer_url: document.referrer || null
      },
      customer: {}
    };

    // Enrich with customer data if logged in (from Liquid injection)
    var cd = window.biesCustomerData;
    if (cd && cd.customer_id) {
      payload.user_data.external_id = String(cd.customer_id);
      payload.customer.id = String(cd.customer_id);
      if (cd.email) { payload.user_data.em = cd.email; }
    }

    // Clean null/undefined from user_data
    Object.keys(payload.user_data).forEach(function(key) {
      if (payload.user_data[key] === null || payload.user_data[key] === undefined) {
        delete payload.user_data[key];
      }
    });

    var COLLECT_URL = 'https://collect-2dwmk4g42a-ew.a.run.app/collect';
    var jsonPayload = JSON.stringify(payload);

    try {
      if (navigator.sendBeacon) {
        var blob = new Blob([jsonPayload], { type: 'application/json' });
        var sent = navigator.sendBeacon(COLLECT_URL, blob);
        if (!sent) {
          fetch(COLLECT_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: jsonPayload,
            keepalive: true
          }).catch(function() {});
        }
      } else {
        fetch(COLLECT_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: jsonPayload,
          keepalive: true
        }).catch(function() {});
      }
      console.log('[BIES Collect] Sent:', metaEventName, eventId);
    } catch(e) {
      console.error('[BIES Collect] Error:', e.message);
    }
  }

  // Expose globally for theme.liquid PageView calls
  window.sendEventToCollect = sendEventToCollect;

  /**
   * Fire fbq('track', 'ViewContent') on product pages when consent is granted.
   * Called from handleAccept() and checkConsentAndShow().
   */
  function fireBrowserPixelForPage() {
    // Only fire once per page
    if (window._biesBrowserPixelFired) return;

    // Need marketing consent (task 03), not just an answered banner
    if (!marketingAllowed()) return;

    // Need fbq
    if (typeof fbq === 'undefined') return;

    // ViewContent on product pages
    var productData = window.__biesProductData;
    if (productData && productData.item_id) {
      window._biesBrowserPixelFired = true;
      var vcData = {
        content_type: 'product',
        content_ids: [metaContentId(productData)],
        content_name: productData.item_name || '',
        value: parseFloat(productData.price) || 0,
        currency: productData.currency || 'EUR'
      };
      var vcEventId = 'evt_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      try {
        Shopify.analytics.publish('bies:event_id_sync', {
          event_name: 'ViewContent_product',
          event_id: vcEventId
        });
      } catch(e) { /* graceful fallback — CAPI still sends with its own event_id */ }
      window.biesFbqTrack('track', 'ViewContent', vcData, {eventID: vcEventId});
      sendEventToCollect('ViewContent', vcEventId, vcData);
      console.log('[BIES Browser Pixel] ViewContent fired + collect sent', vcData, 'eventID:', vcEventId);
    }
  }

  /**
   * Fire fbq('track', 'AddToCart') when user adds a product to cart.
   * Uses PerformanceObserver to detect /cart/add requests (reliable,
   * can't be overwritten by other scripts unlike fetch wrapping).
   * Falls back to click listener on product-form submit button.
   */
  // spec bies-theme-rendimiento-cookies-y-senal-v1 task 04 (reassigned): the previous
  // PerformanceObserver fired AddToCart for ANY request whose URL contained /cart/add —
  // failed adds (422), and the cart drawer's automatic normalize/swap/rollback/recommend
  // fetches — using the PDP default variant instead of what was really added. Now the
  // add paths (theme shim, cart drawer, cart carousel, home bestsellers) emit
  // 'bies:cart:added' only after Shopify answered 2xx, with the returned items and a
  // per-request token. One request → one event; data comes from Shopify's response.
  var _biesAtcSeenTokens = {};

  function setupAddToCartBrowserPixel() {
    document.addEventListener('bies:cart:added', function(event) {
      var detail = event && event.detail;
      if (!detail || !detail.token || !Array.isArray(detail.items) || !detail.items.length) return;
      if (_biesAtcSeenTokens[detail.token]) return; // duplicate emission guard
      _biesAtcSeenTokens[detail.token] = true;
      fireAddToCartPixel(detail);
    });
    console.log('[BIES Browser Pixel] AddToCart listener initialized (bies:cart:added)');
  }

  function fireAddToCartPixel(detail) {
    if (!marketingAllowed() || typeof fbq === 'undefined') return; // task 03 gate
    if (!detail || !Array.isArray(detail.items)) return;

    var items = detail.items;
    var contentIds = [];
    var value = 0;
    for (var i = 0; i < items.length; i++) {
      var line = items[i] || {};
      var vid = line.variant_id || line.id;
      if (vid) contentIds.push(String(vid));
      var unit = (typeof line.final_price === 'number' ? line.final_price
                 : typeof line.price === 'number' ? line.price : 0) / 100; // Shopify returns cents
      value += unit * (parseInt(line.quantity, 10) || 1);
    }
    if (!contentIds.length) return;

    var productData = window.__biesProductData || {};
    var atcData = {
      content_type: 'product',
      content_ids: contentIds,
      content_name: (items[0] && items[0].title) || productData.item_name || '',
      value: Math.round(value * 100) / 100,
      currency: productData.currency || 'EUR',
      num_items: items.length,
      source: detail.source || 'unknown'
    };
    var atcEventId = String(detail.token); // already 'atc_<ts>_<rand>' from the emitter
    try {
      Shopify.analytics.publish('bies:event_id_sync', {
        event_name: 'AddToCart',
        event_id: atcEventId
      });
    } catch(e) { /* graceful fallback — CAPI still sends with its own event_id */ }
    window.biesFbqTrack('track', 'AddToCart', atcData, {eventID: atcEventId});
    sendEventToCollect('AddToCart', atcEventId, atcData);
    console.log('[BIES Browser Pixel] AddToCart fired from', atcData.source, atcData, 'eventID:', atcEventId);
  }

  // ============================================
  // INICIALIZACIÓN
  // ============================================

  function init() {
    // Si no existen los elementos, salir
    if (!els.overlay || !els.banner) {
      console.warn('[BIES Cookies] Banner elements not found');
      return;
    }

    // ============================================
    // REGION & CONSENT STATE
    // ============================================
    var isEU = window._biesIsEU;
    var country = window._biesCountry || 'unknown';
    var hasStoredConsent = hasConsent();
    var consentReason = window._biesConsentReason || 'unknown';

    console.log('[BIES Cookies] Init | EU:', isEU, '| Country:', country, '| Consent:', hasStoredConsent, '| Reason:', consentReason);

    // ============================================
    // SETUP CUSTOM EVENT LISTENERS (for event buffer)
    // Solo eventos custom que NO cubre el Web Pixel
    // ============================================
    if (isEU && !hasStoredConsent) {
      setupCustomEventListeners();
      console.log('[BIES Cookies] Custom event listeners initialized (EU visitor pending consent)');

      // Run chain break detection after setup
      detectChainBreak();
    }

    // CRITICO: Cargar el API de Shopify primero
    if (window.Shopify && window.Shopify.loadFeatures) {
      window.Shopify.loadFeatures([{
        name: 'consent-tracking-api',
        version: '0.1'
      }], (error) => {
        if (error) {
          console.error('[BIES Cookies] Error loading Shopify API:', error);
          checkConsentAndShow();
          return;
        }

        // API cargado correctamente
        console.log('[BIES Cookies] Shopify API loaded');
        checkConsentAndShow();
      });
    } else {
      // Fallback si loadFeatures no existe
      checkConsentAndShow();
    }
  }

  function checkConsentAndShow() {
    // ============================================
    // REGION CHECK: Only show banner to EU visitors
    // ============================================
    var isEU = window._biesIsEU;
    var country = window._biesCountry || 'unknown';

    // Non-EU visitors: Skip banner entirely (auto-granted in theme.liquid)
    if (isEU === false) {
      console.log('[BIES Cookies] Non-EU visitor (' + country + ') - skipping banner, tracking auto-granted');
      hideBanner();

      // Fire browser pixel ViewContent for non-EU visitors (consent auto-granted)
      fireBrowserPixelForPage();

      // Track non-EU auto-accept (once per session)
      try {
        if (!sessionStorage.getItem('_bies_non_eu_tracked')) {
          sessionStorage.setItem('_bies_non_eu_tracked', '1');
          var _neSignals = collectUserSignals();
          fetch('https://consent-2dwmk4g42a-ew.a.run.app', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              consent_id: getConsentId(),
              accepted: true,
              preference: 'auto_non_eu',
              analytics: true,
              marketing: true,
              is_eu: false,
              country: country,
              user_signals: {
                language: _neSignals.language,
                timezone: _neSignals.timezone,
                screen_resolution: _neSignals.screen_resolution,
                viewport: _neSignals.viewport,
                platform: _neSignals.platform,
                referrer: _neSignals.referrer,
                landing_page: window.location.pathname,
                utm_source: _neSignals.utm_source || '',
                utm_medium: _neSignals.utm_medium || '',
                utm_campaign: _neSignals.utm_campaign || ''
              }
            }),
            keepalive: true
          });
        }
      } catch(_e) { /* silent */ }

      return;
    }

    // Check localStorage for existing consent
    const localConsent = hasConsent();

    console.log('[BIES Cookies] EU visitor (' + country + ') | localStorage consent:', localConsent);

    // Si YA aceptó nuestro banner (localStorage), no mostrar
    // pero RE-ENVIAR consentimiento a Shopify (su cookie puede haberse borrado)
    if (localConsent) {
      console.log('[BIES Cookies] Banner ya fue aceptado - ocultando');
      hideBanner();

      if (window.Shopify && window.Shopify.customerPrivacy) {
        var storedAnalytics = localStorage.getItem('bies_cookies_analytics') !== 'false';
        var storedMarketing = localStorage.getItem('bies_cookies_marketing') !== 'false';

        window.Shopify.customerPrivacy.setTrackingConsent({
          analytics: storedAnalytics,
          marketing: storedMarketing,
          preferences: true,
          sale_of_data: storedMarketing
        }, function() {
          console.log('[BIES GDPR] Returning visitor consent re-signaled to Shopify');
        });
      }

      // Fire browser pixel ViewContent for returning visitors on product pages
      fireBrowserPixelForPage();

      // Ensure PageView reached /collect for returning visitors (Astra race condition guard)
      if (marketingAllowed() && !window._biesCollectPageViewSent && window.__bies_pageview_event_id) {
        sendEventToCollect('PageView', window.__bies_pageview_event_id, {});
      }

      return;
    }

    // Check session dismissal (user closed banner without accepting this session)
    var sessionDismissed = false;
    try { sessionDismissed = sessionStorage.getItem('bies_cookies_dismissed') === '1'; } catch(e) {}

    if (sessionDismissed) {
      console.log('[BIES Cookies] Banner dismissed this session - not showing');
      hideBanner();
      return;
    }

    // EU visitor sin consent: mostrar banner
    console.log('[BIES Cookies] EU visitor sin consent - mostrando banner');
    showBanner();

    // Attach event listeners
    if (els.acceptBtn) {
      els.acceptBtn.addEventListener('click', handleAccept);
    }

    if (els.rejectBtn) {
      els.rejectBtn.addEventListener('click', handleReject);
    }

    if (els.settingsBtn) {
      els.settingsBtn.addEventListener('click', handleSettings);
    }

    if (els.saveBtn) {
      els.saveBtn.addEventListener('click', handleSavePreferences);
    }

    if (els.rejectAllBtn) {
      els.rejectAllBtn.addEventListener('click', handleRejectAll);
    }

    // ESC key para cerrar (opcional - NO recomendado si quieres forzar)
    // document.addEventListener('keydown', (e) => {
    //   if (e.key === 'Escape' && els.overlay.classList.contains('show')) {
    //     handleReject();
    //   }
    // });
  }

  // Ejecutar cuando DOM esté listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Exponer API pública (for integrations and debugging)
  window.BiesCookies = {
    // Consent state
    hasConsent: hasConsent,
    getConsentId: function() { return CONSENT_ID; },
    getUserSignals: collectUserSignals,

    // Actions
    accept: handleAccept,
    reject: handleReject,
    openSettings: handleSettings,
    savePreferences: handleSavePreferences,
    rejectAll: handleRejectAll,

    // ============================================
    // EVENT BUFFER API
    // ============================================
    buffer: {
      // Get current buffer stats
      getStats: function() { return EventBuffer.getStats(); },

      // Get full buffer contents
      getEvents: function() { return EventBuffer.getBuffer(); },

      // Manually add event to buffer (for custom tracking)
      addEvent: function(eventName, eventData) {
        return EventBuffer.addEvent(eventName, eventData);
      },

      // Manually flush buffer (sends all events)
      flush: function() { EventBuffer.flushBuffer(); },

      // Clear buffer without sending
      clear: function() { EventBuffer.clearBuffer(); },

      // Get supported event types
      getSupportedEvents: function() { return EventBuffer.SUPPORTED_EVENTS; }
    },

    // ============================================
    // MANUAL ENHANCED ECOMMERCE TRACKING
    // Use these to manually track events when consent exists
    // ============================================
    track: {
      viewItem: function(itemData) {
        if (!hasConsent()) {
          EventBuffer.addEvent('view_item', itemData);
          return 'buffered';
        }
        EventBuffer.sendToAllPlatforms({ name: 'view_item', data: itemData, timestamp: Date.now() });
        return 'sent';
      },

      addToCart: function(itemData) {
        if (!hasConsent()) {
          EventBuffer.addEvent('add_to_cart', itemData);
          return 'buffered';
        }
        EventBuffer.sendToAllPlatforms({ name: 'add_to_cart', data: itemData, timestamp: Date.now() });
        return 'sent';
      },

      beginCheckout: function(checkoutData) {
        if (!hasConsent()) {
          EventBuffer.addEvent('begin_checkout', checkoutData);
          return 'buffered';
        }
        EventBuffer.sendToAllPlatforms({ name: 'begin_checkout', data: checkoutData, timestamp: Date.now() });
        return 'sent';
      },

      purchase: function(purchaseData) {
        if (!hasConsent()) {
          EventBuffer.addEvent('purchase', purchaseData);
          return 'buffered';
        }
        EventBuffer.sendToAllPlatforms({ name: 'purchase', data: purchaseData, timestamp: Date.now() });
        return 'sent';
      },

      search: function(searchTerm) {
        var data = { search_term: searchTerm };
        if (!hasConsent()) {
          EventBuffer.addEvent('search', data);
          return 'buffered';
        }
        EventBuffer.sendToAllPlatforms({ name: 'search', data: data, timestamp: Date.now() });
        return 'sent';
      },

      // Generic event tracker
      custom: function(eventName, eventData) {
        if (!hasConsent()) {
          EventBuffer.addEvent(eventName, eventData);
          return 'buffered';
        }
        EventBuffer.sendToAllPlatforms({ name: eventName, data: eventData, timestamp: Date.now() });
        return 'sent';
      }
    },

    // Reset (for testing)
    reset: function() {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_PREFERENCE);
      EventBuffer.clearBuffer();
      // Note: consent_id is NOT removed - it's permanent
      window.location.reload();
    },

    // Full reset including consent_id (for complete cleanup)
    fullReset: function() {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_PREFERENCE);
      localStorage.removeItem(STORAGE_CONSENT_ID);
      EventBuffer.clearBuffer();
      document.cookie = STORAGE_CONSENT_ID + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      window.location.reload();
    },

    // Link email to consent_id (call when user provides email)
    linkEmail: function(email) {
      if (!email) return;

      // Store locally
      localStorage.setItem('bies_linked_email', email);

      // Also send to Facebook
      if (window.fbq) {
        // Shadow datasets A/A: same init for production + B + B0 (helper in theme.liquid <head>)
        if (typeof window.biesFbqInitAll === 'function') {
          window.biesFbqInitAll(window.FB_PIXEL_ID || null, {
            em: email,
            external_id: CONSENT_ID
          });
        } else {
          fbq('init', window.FB_PIXEL_ID || null, {
            em: email,
            external_id: CONSENT_ID
          });
        }
      }

      console.log('[BIES GDPR] Email linked to consent_id:', CONSENT_ID, email);
    }
  };

  // Log consent_id for debugging
  console.log('[BIES GDPR] Consent ID:', CONSENT_ID);

})();

